import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { OpenAIRealtimeRecorder } from '../components/interview/OpenAIRealtimeRecorder';
import { VoiceRecorder } from '../components/interview/VoiceRecorder';
import { getSilenceThresholdMs } from '../utils/turnDetection';
import { QuestionDisplay } from '../components/interview/QuestionDisplay';
import { ProctoringMonitor } from '../components/proctoring/ProctoringMonitor';
import { AIInterviewer } from '../components/interview/AIInterviewer';
import VideoRecorderWithMixedAudio from '../utils/VideoRecorderWithMixedAudio';
import { Room, createLocalVideoTrack, createLocalAudioTrack } from 'livekit-client';
import '../components/interview/InterviewRoom.css';
import { getPublicApiOrigin, getPublicWsOrigin } from '../config/env';
import {
  claimInterviewResources,
  getClaimedInterviewResources,
  isStreamLive,
  releaseClaimedInterviewResources,
  stashContinuousSessionHandoff,
  isObjectDetectionReady,
  waitForObjectDetectionReady,
  prewarmObjectDetection,
} from '../utils/interviewPrep';
import { useBrowserSecurity } from '../hooks/useBrowserSecurity';
import {
  isFullscreenActive,
  requestExamFullscreen,
  addFullscreenChangeListener,
  requestEntireScreenShare,
} from '../utils/fullscreen';
import {
  attachAudioElementAsReference,
  closeSharedAudioContext,
} from '../utils/audioEchoCancellation';

/** Keeps TTS element off-screen but *not* display:none — some browsers mute inaudible hidden media. */
function configureHiddenQuestionAudio(el) {
  if (!el) return;
  el.crossOrigin = 'anonymous';
  el.setAttribute('playsInline', '');
  el.volume = 1;
  el.muted = false;
  Object.assign(el.style, {
    position: 'fixed',
    left: '-9999px',
    top: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    overflow: 'hidden',
  });
}

/** Play Edge TTS audio (base64 MP3) on the shared question audio element — same voice as questions. */
function playQuestionAudio(base64Audio, audioEl, cancelledRef, rate = 1) {
  return new Promise((resolve) => {
    if (!base64Audio || !audioEl || cancelledRef?.current) {
      resolve();
      return;
    }
    if (base64Audio.length < 100) {
      resolve();
      return;
    }
    let objectUrl = null;
    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      audioEl.onended = null;
      audioEl.onerror = null;
      audioEl.oncanplay = null;
      // Reset unconditionally — the shared element is reused by every
      // later clip, so a non-default rate from a speak-slower/faster
      // request must never silently carry over onto the next turn.
      audioEl.playbackRate = 1;
    };
    try {
      audioEl.pause();
      audioEl.currentTime = 0;
      audioEl.playbackRate = rate;
      const audioData = atob(base64Audio);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const blob = new Blob([audioArray], { type: 'audio/mpeg' });
      objectUrl = URL.createObjectURL(blob);
      audioEl.src = objectUrl;
      // play() resolving only means playback STARTED, not that it finished —
      // resolving this promise there (as this used to) let a caller chain
      // like playQuestionAudio(ack).then(() => playQuestionAudio(main)) fire
      // almost immediately after the first clip began, overwriting the
      // shared element's src mid-playback and cutting it off after a
      // fraction of a second. Only onended/onerror should resolve; a
      // rejected play() is the one case worth resolving early, since
      // nothing will ever call onended for a clip that never started.
      audioEl.oncanplay = () => {
        audioEl.play().catch(() => resolve());
      };
      audioEl.onended = () => {
        cleanup();
        resolve();
      };
      audioEl.onerror = () => {
        cleanup();
        resolve();
      };
      audioEl.load();
    } catch (e) {
      console.warn('playQuestionAudio failed:', e);
      cleanup();
      resolve();
    }
  });
}

/** Play closing TTS on the shared question audio element; waits until playback ends. */
function playClosingTtsBlob(base64Audio, audioEl) {
  return new Promise((resolve) => {
    if (!base64Audio || !audioEl || base64Audio.length < 100) {
      resolve();
      return;
    }
    let objectUrl = null;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      audioEl.onended = null;
      audioEl.onerror = null;
      resolve();
    };
    const safetyTimer = setTimeout(finish, 15000);
    try {
      audioEl.pause();
      audioEl.currentTime = 0;
      audioEl.onended = null;
      audioEl.onerror = null;
      audioEl.oncanplay = null;
      const audioData = atob(base64Audio);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const blob = new Blob([audioArray], { type: 'audio/mpeg' });
      if (blob.size === 0) {
        clearTimeout(safetyTimer);
        finish();
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      audioEl.src = objectUrl;
      audioEl.onended = () => {
        clearTimeout(safetyTimer);
        finish();
      };
      audioEl.onerror = () => {
        clearTimeout(safetyTimer);
        finish();
      };
      audioEl.load();
      audioEl.play().catch(() => {
        clearTimeout(safetyTimer);
        finish();
      });
    } catch (e) {
      console.warn('playClosingTtsBlob failed:', e);
      clearTimeout(safetyTimer);
      finish();
    }
  });
}

const CLOSING_MESSAGE =
  'Thank you for completing your interview. We will review your responses and be in touch soon.';

/** Check if candidate utterance indicates intent to end/finish/close the interview session. */
function isVoiceEndInterviewUtterance(text) {
  if (!text || typeof text !== 'string') return false;
  const normalized = text.toLowerCase().replace(/[^\w\s']/g, ' ').replace(/\s+/g, ' ').trim();
  if (!normalized) return false;

  return (
    /\b(can (we|i)|could (we|i)|let'?s|let us|i want to|i'?d like to|i would like to|please)\s*(end|finish|stop|close|quit|wrap up|terminate|leave|exit|conclude)\s*(the|this|my)?\s*(interview|exam|session|call|test|assessment)\b/i.test(normalized) ||
    /\b(end|finish|stop|close|quit|wrap up|terminate|conclude)\s*(the|this)\s*(interview|exam|session|call|test|assessment)\b/i.test(normalized) ||
    /^(end|finish|stop|quit)\s+(the\s+|this\s+)?(interview|exam|session)$/i.test(normalized) ||
    /\bi('?m| am) done with (this|the) (interview|exam|session|call|test|assessment)\b/i.test(normalized) ||
    /\bi don'?t want to continue (with )?(this|the)?\s*(interview|exam|session|call|test|assessment)\b/i.test(normalized) ||
    /\b(can (we|i)|let'?s|let us|could we|i want to)\s+(stop|end|finish)\s+here\b/i.test(normalized) ||
    /^(stop|end|finish)\s+here$/i.test(normalized)
  );
}

const CandidateInterviewRoom = ({
  sessionIdFromLanding,
  prepResources: prepFromParent,
  isResuming = false,
  fullscreenRequired: fullscreenRequiredProp = false,
  expectsCodingPhase = false,
  onContinueToCoding = null,
}) => {
  const { sessionId: sessionIdFromParams } = useParams();
  const sessionId = sessionIdFromLanding || sessionIdFromParams;
  const navigate = useNavigate();
  const prepResourcesRef = useRef(
    prepFromParent || getClaimedInterviewResources() || claimInterviewResources()
  );
  const prepResources = prepResourcesRef.current;
  const hasLivePrewarm = isStreamLive(prepResources?.stream);
  const [closingTtsPreparing, setClosingTtsPreparing] = useState(false);
  const [interviewStatus, setInterviewStatus] = useState(isResuming ? 'in_progress' : 'loading');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [raniSpeaking, setRaniSpeaking] = useState(false);
  const raniSpeakingRef = useRef(false);
  const [useLocalSttFallback, setUseLocalSttFallback] = useState(false);
  const handleRealtimeSttError = useCallback((err) => {
    console.warn('⚠️ OpenAI Realtime STT unavailable/failed — automatically switching to Local Browser STT (VoiceRecorder):', err);
    setUseLocalSttFallback(true);
  }, []);
  // Accumulates the text of every AI clip spoken over the course of the
  // interview — intro, every transition/ack, every question/follow-up.
  // Passed to the active recorder (OpenAIRealtimeRecorder, formerly
  // VoiceRecorder) as lastSpokenText so it can tell a genuine
  // early-start answer apart from the AI's own audio leaking into the mic
  // (real capture: a follow-up's transition+question text got picked up
  // and folded into the candidate's next answer as if they'd said it).
  // Deliberately never cleared: a fold-time match against ANYTHING the AI
  // has ever said is still a correct "this looks like an echo" signal, and
  // that avoids a real ordering hazard a per-turn reset would have —
  // acks/nudges (handleAdvisory) can play *within* the same aiSpeaking
  // window a next_question's own reset would otherwise wipe out before the
  // one-time fold-time comparison ever reads it. Bounded growth over a
  // normal-length interview (a handful of KB of text) is a non-issue.
  const lastSpokenTextRef = useRef('');
  const [questionTransition, setQuestionTransition] = useState('in');
  const [raniVideoUrl, setRaniVideoUrl] = useState(null);
  const [questionAudioReady, setQuestionAudioReady] = useState(false);
  // Removed audioChunks - using transcripts directly now
  const [notifications, setNotifications] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(1);
  const [waitingForAnswer, setWaitingForAnswer] = useState(false);
  const [idleSeconds, setIdleSeconds] = useState(0);
  const idleTimerRef = useRef(null);
  const [candidateName, setCandidateName] = useState('');
  const [processingAnswer, setProcessingAnswer] = useState(false);
  // Server-sent, purely elapsed-time "I'm still waiting on the signal I
  // expected from you, but I'm alive" status while the backend sits in
  // ASKING waiting for playback_ended (or any future client confirmation).
  // Not tied to a question, audio file, or failure cause. Rendered as a
  // persistent status label on the interviewer (not a 3s toast) so a
  // candidate who looks up 20s later still sees it; cleared the moment the
  // interview actually progresses.
  const [systemWorkingNote, setSystemWorkingNote] = useState(null);
  const [davidSpeechTranscript, setDavidSpeechTranscript] = useState(null);
  const activeAckPlaybackPromiseRef = useRef(null);
  const isPlayingProcessingAckRef = useRef(false);
  const processingAckPlayedThisTurnRef = useRef(false);
  const processingAckPoolRef = useRef(null);
  const wrapUpAckPoolRef = useRef(null);
  const timeoutAckPoolRef = useRef(null);
  const isTurnTimeoutRef = useRef(false);
  const lastAckStyleIndexRef = useRef(-1);
  const [showingClosingTTS, setShowingClosingTTS] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenActive());
  const [fullscreenRequired, setFullscreenRequired] = useState(!!fullscreenRequiredProp);
  const [showScreenShareGate, setShowScreenShareGate] = useState(
    !isStreamLive(prepResources?.screenStream)
  );
  const [showEndConfirmModal, setShowEndConfirmModal] = useState(false);
  const [controlMicOn, setControlMicOn] = useState(true);
  const [controlCamOn, setControlCamOn] = useState(true);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const transcriptAccumulatorRef = useRef(''); // Accumulate all final transcripts
  const currentQuestionIdRef = useRef(null);
  const transcriptQuestionIdRef = useRef(null);
  const currentQuestionSeqRef = useRef(0);
  // Count of real main questions actually shown — deliberately separate from
  // currentQuestionSeqRef. sequence_number is a raw, monotonic row counter
  // that climbs on every follow-up/clarify retry too, not just main
  // questions (2026-09-15 repro, session efa93e37: 2 follow-ups on Q1 alone
  // pushed sequence_number to 3 == totalQuestions, so the "already at last
  // question" guard below silently dropped the real Q2 for the rest of the
  // interview). Only ever incremented for a next_question whose is_follow_up
  // is false — see handleNextQuestion.
  const mainQuestionsShownRef = useRef(0);
  const answerQuestionIdRef = useRef(null);
  const recordingCountdownRef = useRef(null);
  const endAnswerSentRef = useRef(false);
  // Throttle gate for the interim-result keepalive (transcript_activity).
  // Some browsers emit interim SpeechRecognition results several times a
  // second; this caps the keepalive to one per ACTIVITY_PING_MIN_INTERVAL_MS
  // so a long answer never floods the socket. No transcript text is ever
  // sent on this path — see the interim branch of handleTranscript.
  const lastActivityPingAtRef = useRef(0);
  const ACTIVITY_PING_MIN_INTERVAL_MS = 750;
  // Mirror of waitingForAnswer state as a ref — lets handleTranscript check the
  // always-current value without capturing a stale closure from useCallback.
  const waitingForAnswerRef = useRef(false);
  const totalQuestionsRef = useRef(1);
  const [recordingSecondsLeft, setRecordingSecondsLeft] = useState(null);
  const handleEndAnswerRef = useRef(() => {});
  // Always-current socket handle for [] -dep callbacks (beginListeningForAnswer)
  // that can't put `socket` in their own deps without recreating on every
  // reconnect — kept in sync below, right after useWebSocket() runs.
  const socketRef = useRef(null);
  // Bridges handleAudioBlob's async Whisper call (driven by VoiceRecorder's
  // MediaRecorder, on its own schedule) to handleEndAnswer's decision to
  // wait for it — set right before this turn's recording is stopped, only
  // when the live browser transcript looked too weak/short to trust as-is.
  // null means nobody is waiting (the common case — most turns never touch
  // this at all).
  const whisperPendingRef = useRef(null);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [isWhisperLoading, setIsWhisperLoading] = useState(false);
  const enteringReviewRef = useRef(false);
  const videoRecorderRef = useRef(null); // Reference to video recorder
  const videoElementRef = useRef(null); // Reference to video element for recording
  const questionAudioElementRef = useRef(null); // Reference to question audio element (for recording)
  const expectsCodingPhaseRef = useRef(!!expectsCodingPhase);
  expectsCodingPhaseRef.current = !!expectsCodingPhase;
  const activeTransitionAudioRef = useRef(null); // Ephemeral transition/closing TTS (new Audio())
  const speakingListenersAttachedRef = useRef(false);
  const recorderInitializedRef = useRef(false); // Track if recorder is initialized
  const livekitRoomRef = useRef(null);
  /** True after user confirms "End interview" — blocks late questions & transition speech */
  const interviewEndedByUserRef = useRef(false);
  /** True after interview_completed (or intentional end) — suppress post-close WebSocket errors */
  const interviewCompletedRef = useRef(false);
  /** Prevents double End Interview clicks while end flow is running */
  const endingInterviewRef = useRef(false);
  /** TTS prefetch started on End Interview confirm — awaited when interview_completed arrives */
  const closingTtsPromiseRef = useRef(null);
  const fullscreenRequestedRef = useRef(false);
  const fullscreenRequiredRef = useRef(!!fullscreenRequiredProp);
  const intentionalExitRef = useRef(false);
  const recorderInitializingRef = useRef(false);
  const screenStreamRef = useRef(prepResources?.screenStream || null);
  const [isEndingInterview, setIsEndingInterview] = useState(false);

  useEffect(() => {
    fullscreenRequiredRef.current = fullscreenRequired;
  }, [fullscreenRequired]);

  useEffect(() => {
    if (fullscreenRequiredProp) {
      setFullscreenRequired(true);
      fullscreenRequiredRef.current = true;
    }
  }, [fullscreenRequiredProp]);

  useEffect(() => {
    raniSpeakingRef.current = raniSpeaking;
  }, [raniSpeaking]);

  useEffect(() => {
    waitingForAnswerRef.current = waitingForAnswer;
  }, [waitingForAnswer]);

  useEffect(() => {
    totalQuestionsRef.current = totalQuestions;
  }, [totalQuestions]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getPublicApiOrigin()}/api/v1/sessions/${sessionId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled && data.candidate_name) {
          setCandidateName(data.candidate_name);
        }
        if (!cancelled && data.fullscreen_required !== undefined) {
          setFullscreenRequired(!!data.fullscreen_required);
        }
      } catch (_) {
        /* optional */
      }

      // Pre-warm 5-style Stage 1 processing ACK pool for zero-latency (<15ms) thinking filler
      try {
        const ackRes = await fetch(`${getPublicApiOrigin()}/api/v1/sessions/${sessionId}/processing-ack-pool`);
        if (ackRes.ok && !cancelled) {
          const ackData = await ackRes.json();
          if (ackData?.styles) {
            processingAckPoolRef.current = ackData.styles;
            console.log('✅ Pre-warmed Stage 1 processing ACK pool loaded:', Object.keys(ackData.styles));
          }
          if (ackData?.wrap_up && ackData.wrap_up.length > 0) {
            wrapUpAckPoolRef.current = ackData.wrap_up;
            console.log('✅ Pre-warmed wrap-up ACK pool loaded:', ackData.wrap_up.map((w) => w.text));
          }
          if (ackData?.timeout && ackData.timeout.length > 0) {
            timeoutAckPoolRef.current = ackData.timeout;
            console.log('✅ Pre-warmed timeout ACK pool loaded:', ackData.timeout.map((w) => w.text));
          }
        }
      } catch (err) {
        console.warn('⚠️ Could not load processing ACK pool:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const showNotification = useCallback((message) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, []);

  const reportProctoringViolation = useCallback(async (eventType, message, severity = 'medium') => {
    const token = localStorage.getItem('access_token');
    if (!token || !sessionId) return;
    try {
      await fetch(`${getPublicApiOrigin()}/api/proctoring/${sessionId}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_type: eventType,
          severity,
          metadata: {
            message,
            videoOffsetSeconds: recordingStartTime
              ? parseFloat(((Date.now() - recordingStartTime) / 1000).toFixed(3))
              : null,
          },
        }),
      });
    } catch (e) {
      console.warn('Failed to report proctoring event:', e);
    }
  }, [sessionId, recordingStartTime]);

  const requestInterviewFullscreen = useCallback(async () => {
    if (fullscreenRequestedRef.current && isFullscreenActive()) return true;
    fullscreenRequestedRef.current = true;
    const ok = await requestExamFullscreen(document.documentElement);
    if (ok || isFullscreenActive()) {
      setIsFullscreen(true);
      return true;
    }
    const onGesture = async () => {
      try {
        await requestExamFullscreen(document.documentElement);
      } catch (_) { /* ignore */ }
    };
    document.addEventListener('click', onGesture, { once: true });
    document.addEventListener('keydown', onGesture, { once: true });
    return false;
  }, []);

  const handleRestoreScreenShare = useCallback(async () => {
    try {
      const displayStream = await requestEntireScreenShare();
      screenStreamRef.current?.getTracks?.().forEach((t) => t.stop());
      screenStreamRef.current = displayStream;
      // Recording switches to the screen after the intro minute — keep it on the live share.
      videoRecorderRef.current?.setScreenStream?.(displayStream);
      setShowScreenShareGate(false);
      const track = displayStream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          screenStreamRef.current = null;
          setShowScreenShareGate(true);
          if (!interviewCompletedRef.current) {
            void reportProctoringViolation(
              'screen_share_stopped',
              'Screen sharing was stopped',
              'critical'
            );
            showNotification('⚠️ Screen sharing is required — please share your entire screen again');
          }
        };
      }
      return true;
    } catch (err) {
      const msg = err?.code === 'NOT_ENTIRE_SCREEN'
        ? err.message
        : 'Screen sharing is required to continue the interview.';
      void reportProctoringViolation('screen_share_denied', msg, 'critical');
      showNotification(`⚠️ ${msg}`);
      return false;
    }
  }, [reportProctoringViolation, showNotification]);

  const handleBrowserViolation = useCallback((violation) => {
    if (interviewCompletedRef.current || intentionalExitRef.current) return;
    if (violation?.type === 'fullscreen_exit') {
      setIsFullscreen(false);
      showNotification('⚠️ Fullscreen is required — returning you to fullscreen');
    }
    // Avoid double-logging fullscreen_exit if restore path already logged via report
    void reportProctoringViolation(
      violation.type,
      violation.message || violation.type,
      violation.severity || 'medium'
    );
  }, [reportProctoringViolation, showNotification]);

  useBrowserSecurity(handleBrowserViolation, {
    enabled: interviewStatus === 'ready' || interviewStatus === 'in_progress',
    blockCopyPaste: true,
    onRestoreFullscreen: () => {
      if (interviewCompletedRef.current || intentionalExitRef.current) return;
      setIsFullscreen(false);
      void requestInterviewFullscreen();
    },
  });

  useEffect(() => {
    // Prefer fullscreen already entered on System Check "Start" click.
    if (isFullscreenActive()) {
      fullscreenRequestedRef.current = true;
      setIsFullscreen(true);
    } else {
      void requestInterviewFullscreen();
    }
    const sync = () => {
      const inFullscreen = isFullscreenActive();
      setIsFullscreen(inFullscreen);
      if (intentionalExitRef.current) {
        intentionalExitRef.current = false;
        return;
      }
      if (!inFullscreen && fullscreenRequestedRef.current && !interviewCompletedRef.current) {
        void requestInterviewFullscreen();
      }
    };
    return addFullscreenChangeListener(sync);
  }, [requestInterviewFullscreen]);

  useEffect(() => {
    const existing = screenStreamRef.current;
    if (!isStreamLive(existing)) return undefined;
    setShowScreenShareGate(false);
    const track = existing.getVideoTracks?.()[0];
    if (!track) return undefined;
    const onEnded = () => {
      screenStreamRef.current = null;
      setShowScreenShareGate(true);
      if (!interviewCompletedRef.current) {
        void reportProctoringViolation(
          'screen_share_stopped',
          'Screen sharing was stopped',
          'critical'
        );
        showNotification('⚠️ Screen sharing is required — please share your entire screen again');
      }
    };
    track.addEventListener('ended', onEnded);
    return () => track.removeEventListener('ended', onEnded);
  }, [reportProctoringViolation, showNotification]);

  useEffect(() => {
    return () => {
      // Screen stream lifecycle is owned by claim/release helpers.
    };
  }, []);
  
  const accessToken = localStorage.getItem('access_token');

  const getTabSessionToken = () => {
    try {
      const raw = localStorage.getItem('wiztok_active_session');
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      return parsed?.token || '';
    } catch {
      return '';
    }
  };
  
  // If used from CandidateLanding (sessionIdFromLanding prop), skip token check
  // The token should already be set from registration
  const isFromLanding = !!sessionIdFromLanding;

  // Only create WebSocket URL if we have both token and sessionId
  const tabSessionToken = getTabSessionToken();
  const wsUrl = (accessToken && sessionId)
    ? `${getPublicWsOrigin()}/ws/session/${sessionId}?token=${encodeURIComponent(accessToken)}${tabSessionToken ? `&session_token=${encodeURIComponent(tabSessionToken)}` : ''}`
    : '';
  
  // Don't reconnect if interview is completed
  const shouldReconnect = interviewStatus !== 'completed' && !showGoodbye;
  
  // Only connect WebSocket if we have a valid URL (empty string prevents connection)
  const { socket, connected } = useWebSocket(wsUrl, shouldReconnect);
  useEffect(() => {
    socketRef.current = connected ? socket : null;
  }, [socket, connected]);

  // Connection lost mid-interview: the socket retries on its own and answers are already
  // saved, but the candidate must know not to close the tab (the recording keeps queueing).
  const everConnectedRef = useRef(false);
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    if (connected) everConnectedRef.current = true;
  }, [connected]);
  useEffect(() => {
    const on = () => setBrowserOnline(true);
    const off = () => setBrowserOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const stopAllInterviewAudio = useCallback(() => {
    const qa = questionAudioElementRef.current;
    if (qa) {
      try {
        qa.pause();
        qa.onended = null;
        qa.onerror = null;
        qa.oncanplay = null;
        qa.src = '';
        qa.load();
      } catch (_) {
        /* ignore */
      }
    }
    const transitionAudio = activeTransitionAudioRef.current;
    if (transitionAudio) {
      try {
        transitionAudio.pause();
        transitionAudio.onended = null;
        transitionAudio.onerror = null;
        transitionAudio.src = '';
      } catch (_) {
        /* ignore */
      }
      activeTransitionAudioRef.current = null;
    }
    setIsSpeaking(false);
    setRaniSpeaking(false);
  }, []);

  const prefetchClosingTts = useCallback(() => {
    if (!sessionId) return null;
    const token = localStorage.getItem('access_token');
    return fetch(`${getPublicApiOrigin()}/api/v1/sessions/${sessionId}/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text: CLOSING_MESSAGE }),
    });
  }, [sessionId]);

  const releaseAllMediaStreams = useCallback(() => {
    const streams = new Set();
    const prepStream = prepResourcesRef.current?.stream;
    if (prepStream instanceof MediaStream) streams.add(prepStream);
    const prepScreenStream = prepResourcesRef.current?.screenStream;
    if (prepScreenStream instanceof MediaStream) streams.add(prepScreenStream);
    if (screenStreamRef.current instanceof MediaStream) {
      streams.add(screenStreamRef.current);
    }
    if (videoElementRef.current?.srcObject instanceof MediaStream) {
      streams.add(videoElementRef.current.srcObject);
    }
    document.querySelectorAll('video').forEach((video) => {
      if (video.srcObject instanceof MediaStream) streams.add(video.srcObject);
    });
    document.querySelectorAll('audio').forEach((audio) => {
      if (audio.srcObject instanceof MediaStream) streams.add(audio.srcObject);
    });
    streams.forEach((stream) => {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
        console.log('🛑 Stopped track:', track.kind);
      });
    });
    if (screenStreamRef.current) screenStreamRef.current = null;
    if (videoElementRef.current) videoElementRef.current.srcObject = null;
    if (prepResourcesRef.current?.stream) {
      prepResourcesRef.current.stream = null;
    }
    if (prepResourcesRef.current?.screenStream) {
      prepResourcesRef.current.screenStream = null;
    }
    const rec = videoRecorderRef.current;
    if (rec && typeof rec.cleanup === 'function') {
      rec.cleanup();
    }
    videoRecorderRef.current = null;
    if (livekitRoomRef.current) {
      try {
        livekitRoomRef.current.localParticipant?.tracks?.forEach((pub) => {
          try { pub.track?.stop?.(); } catch (_) {}
        });
        livekitRoomRef.current.disconnect();
      } catch (_) {}
      livekitRoomRef.current = null;
    }
    closeSharedAudioContext();
    releaseClaimedInterviewResources();
  }, []);

  // Ensure all media streams, tracks, and audio contexts are fully stopped upon interview completion.
  // AI+coding: keep camera/recorder alive through the goodbye → coding handoff.
  useEffect(() => {
    if (expectsCodingPhaseRef.current) return;
    if (interviewStatus === 'completed' || showGoodbye) {
      releaseAllMediaStreams();
    }
  }, [interviewStatus, showGoodbye, releaseAllMediaStreams]);

  // Teardown cleanup on unmount. AI+coding stash is handled by the recorder effect below —
  // do not releaseClaimedInterviewResources here or the continuous recorder is killed.
  useEffect(() => {
    return () => {
      if (expectsCodingPhaseRef.current) return;
      releaseAllMediaStreams();
    };
  }, [releaseAllMediaStreams]);

  const ensureQuestionAudioElement = useCallback(() => {
    if (!questionAudioElementRef.current) {
      questionAudioElementRef.current = document.createElement('audio');
      configureHiddenQuestionAudio(questionAudioElementRef.current);
      document.body.appendChild(questionAudioElementRef.current);
      attachAudioElementAsReference(questionAudioElementRef.current);
      console.log('✅ Question TTS audio element created & linked to AEC reference');
    } else {
      configureHiddenQuestionAudio(questionAudioElementRef.current);
      attachAudioElementAsReference(questionAudioElementRef.current);
    }

    if (!speakingListenersAttachedRef.current && questionAudioElementRef.current) {
      speakingListenersAttachedRef.current = true;
      const el = questionAudioElementRef.current;
      el.addEventListener('play', () => {
        if (!isPlayingProcessingAckRef.current) {
          setIsSpeaking(true);
          setRaniSpeaking(true);
        }
      });
      el.addEventListener('ended', () => {
        setIsSpeaking(false);
        setRaniSpeaking(false);
      });
      el.addEventListener('pause', () => {
        // Only mark as not speaking if the audio actually ended —
        // not if browser paused it (e.g. tab switch)
        if (el.ended || el.currentTime === 0) {
          setIsSpeaking(false);
          setRaniSpeaking(false);
        }
        // If paused but not ended — browser throttled it (tab switch)
        // Keep raniSpeaking true so waitForThankYou doesn't resolve early
      });
    }

    setQuestionAudioReady(true);
  }, []);

  const playTransitionAudio = useCallback((base64Audio, transcriptText = null) => {
    return new Promise((resolve) => {
      if (interviewEndedByUserRef.current || interviewCompletedRef.current) {
        resolve();
        return;
      }
      setRaniSpeaking(true);
      if (transcriptText) {
        setDavidSpeechTranscript(transcriptText);
      }
      const finish = () => {
        setRaniSpeaking(false);
        setDavidSpeechTranscript(null);
        resolve();
      };
      try {
        ensureQuestionAudioElement();
        if (questionAudioElementRef.current) {
          playQuestionAudio(base64Audio, questionAudioElementRef.current, interviewEndedByUserRef)
            .then(() => finish())
            .catch(() => finish());
        } else {
          finish();
        }
      } catch (e) {
        finish();
      }
    });
  }, [ensureQuestionAudioElement]);

  const playInstantThinkingAck = useCallback(() => {
    if (interviewEndedByUserRef.current || processingAckPlayedThisTurnRef.current) return;
    const pool = processingAckPoolRef.current;
    if (!pool) return;

    const styleNames = Object.keys(pool).filter((k) => pool[k] && pool[k].length > 0);
    if (styleNames.length === 0) return;

    // Anti-repetition: advance to next style index so consecutive turns never reuse the same style
    let nextIdx = (lastAckStyleIndexRef.current + 1) % styleNames.length;
    lastAckStyleIndexRef.current = nextIdx;
    const chosenStyle = styleNames[nextIdx];
    const variants = pool[chosenStyle];
    const picked = variants[Math.floor(Math.random() * variants.length)];
    if (!picked || !picked.audio) return;

    console.log(`🎙️ Instant Thinking ACK [style: ${chosenStyle}]: "${picked.text}"`);
    processingAckPlayedThisTurnRef.current = true;
    ensureQuestionAudioElement();
    if (questionAudioElementRef.current) {
      if (picked.text) lastSpokenTextRef.current += picked.text + ' ';
      isPlayingProcessingAckRef.current = true;
      if (picked.text) setDavidSpeechTranscript(picked.text);
      const p = playQuestionAudio(picked.audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      }).catch(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      });
      activeAckPlaybackPromiseRef.current = p;
    }
  }, [ensureQuestionAudioElement]);

  const playInstantWrapUpAck = useCallback(() => {
    if (interviewEndedByUserRef.current || processingAckPlayedThisTurnRef.current) return;
    const variants = wrapUpAckPoolRef.current;
    if (!variants || variants.length === 0) return;

    const picked = variants[Math.floor(Math.random() * variants.length)];
    if (!picked || !picked.audio) return;

    console.log(`🎙️ Instant Wrap-Up ACK: "${picked.text}"`);
    processingAckPlayedThisTurnRef.current = true;
    ensureQuestionAudioElement();
    if (questionAudioElementRef.current) {
      if (picked.text) lastSpokenTextRef.current += picked.text + ' ';
      isPlayingProcessingAckRef.current = true;
      if (picked.text) setDavidSpeechTranscript(picked.text);
      const p = playQuestionAudio(picked.audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      }).catch(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      });
      activeAckPlaybackPromiseRef.current = p;
    }
  }, [ensureQuestionAudioElement]);

  const playInstantTimeoutAck = useCallback(() => {
    if (interviewEndedByUserRef.current) return;
    const variants = timeoutAckPoolRef.current;
    if (!variants || variants.length === 0) return;

    const picked = variants[Math.floor(Math.random() * variants.length)];
    if (!picked || !picked.audio) return;

    console.log(`🎙️ Instant Timeout ACK: "${picked.text}"`);
    processingAckPlayedThisTurnRef.current = true;
    ensureQuestionAudioElement();
    if (questionAudioElementRef.current) {
      if (picked.text) lastSpokenTextRef.current += picked.text + ' ';
      isPlayingProcessingAckRef.current = true;
      if (picked.text) setDavidSpeechTranscript(picked.text);
      const p = playQuestionAudio(picked.audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      }).catch(() => {
        isPlayingProcessingAckRef.current = false;
        setDavidSpeechTranscript(null);
        if (activeAckPlaybackPromiseRef.current === p) {
          activeAckPlaybackPromiseRef.current = null;
        }
      });
      activeAckPlaybackPromiseRef.current = p;
    }
  }, [ensureQuestionAudioElement]);

  // Ensure shared audio element exists on mount (before first WS message).
  // Also initiate sliced, watchdog-safe loading of AI object detection inside the room.
  useEffect(() => {
    ensureQuestionAudioElement();
    void prewarmObjectDetection();
  }, [ensureQuestionAudioElement]);

  // ✅ FEATURE 4: Initialize video recording when video stream is ready
  const handleVideoReady = useCallback((videoElement) => {
    console.log('📹 handleVideoReady called', {
      sessionId: !!sessionId,
      videoElement: !!videoElement,
      interviewStatus,
      videoElementSrcObject: !!videoElement?.srcObject,
    });

    if (!sessionId) {
      console.error('❌ Cannot set up video: sessionId missing');
      return;
    }

    if (!videoElement) {
      console.error('❌ Cannot set up video: videoElement missing');
      return;
    }

    const stream = videoElement.srcObject;
    if (!stream) {
      console.error('❌ Cannot set up video: videoElement.srcObject is null');
      console.log('🔄 Waiting for stream to be available...');
      setTimeout(() => {
        if (videoElement.srcObject && !videoElementRef.current?.srcObject) {
          handleVideoReady(videoElement);
        }
      }, 1000);
      return;
    }

    if (!stream.active) {
      console.error('❌ Cannot set up video: stream is not active');
      return;
    }

    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length === 0) {
      console.error('❌ Cannot set up video: no video tracks in stream');
      return;
    }

    console.log('✅ Stream validated', {
      streamActive: stream.active,
      videoTracks: videoTracks.length,
      audioTracks: stream.getAudioTracks().length,
      videoTrackEnabled: videoTracks[0]?.enabled,
      videoTrackReadyState: videoTracks[0]?.readyState,
    });

    if (!videoElementRef.current) {
      videoElementRef.current = document.createElement('video');
      videoElementRef.current.autoplay = true;
      videoElementRef.current.playsInline = true;
      videoElementRef.current.muted = true;
      videoElementRef.current.style.display = 'none';
      document.body.appendChild(videoElementRef.current);
      console.log('✅ Created hidden video element for recording');
    }

    videoElementRef.current.srcObject = stream;
    console.log('✅ Stream assigned to video element — starting recorder init');

    const initializeRecorder = async () => {
      if (
        videoRecorderRef.current ||
        recorderInitializedRef.current ||
        recorderInitializingRef.current
      ) {
        console.log('⚠️ Video recorder already initialized or initializing');
        return;
      }

      recorderInitializingRef.current = true;
      ensureQuestionAudioElement();

      console.log('🎬 Initializing VideoRecorderWithMixedAudio...');
      const recorder = new VideoRecorderWithMixedAudio(sessionId);
      videoRecorderRef.current = recorder;
      console.log('✅ videoRecorderRef.current set (before init)');

      try {
        await recorder.initialize(
          videoElementRef.current,
          questionAudioElementRef.current,
          { screenStream: screenStreamRef.current },
        );
        recorderInitializedRef.current = true;
        console.log('✅ Video recorder initialized and ready (will start on first question)');
      } catch (error) {
        console.error('❌ Failed to initialize video recorder:', error);
        videoRecorderRef.current = null;
        recorderInitializedRef.current = false;
      } finally {
        recorderInitializingRef.current = false;
      }
    };

    void initializeRecorder();
  }, [sessionId, ensureQuestionAudioElement]);

  useEffect(() => {
    return () => {
      const rec = videoRecorderRef.current;
      // Continuous AI→coding: leave MediaRecorder running; coding room adopts it.
      if (expectsCodingPhaseRef.current) {
        if (rec) {
          stashContinuousSessionHandoff({
            videoRecorder: rec,
            recordingStartTime: rec.getRecordingStartTime?.() || null,
            stream: prepResourcesRef.current?.stream || videoElementRef.current?.srcObject || null,
          });
          videoRecorderRef.current = null;
        }
        return;
      }
      if (rec) {
        // Do not call cleanup() here — it clears chunks while onstop may still be uploading.
        if (typeof rec.stopAsync === 'function') {
          void rec.stopAsync().catch(() => {});
        } else {
          rec.stop();
        }
      }
      if (livekitRoomRef.current) {
        try {
          livekitRoomRef.current.disconnect();
        } catch (e) {
          console.warn('LiveKit disconnect:', e);
        }
        livekitRoomRef.current = null;
      }
      // Clean up hidden video element
      if (videoElementRef.current && videoElementRef.current.parentNode) {
        videoElementRef.current.parentNode.removeChild(videoElementRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleConnected = () => {
      if (interviewCompletedRef.current) return;
      console.log('✅ Connected to interview session');
      interviewEndedByUserRef.current = false;
      setInterviewStatus('ready');
    };

    const handleError = (error) => {
      if (interviewCompletedRef.current || interviewEndedByUserRef.current) {
        return;
      }
      console.error('❌ Interview error:', error);
      const errorMsg = error?.message || error?.data?.message || 'Unknown error';
      alert(`Error: ${errorMsg}`);
      setInterviewStatus('error');
    };

    const handleDisconnect = () => {
      if (interviewCompletedRef.current || interviewEndedByUserRef.current) {
        return;
      }
      console.log('WebSocket disconnected');
    };

    const handleQuestion = async (data) => {
      if (interviewEndedByUserRef.current) {
        console.log('🛑 Ignoring question — interview ended by user');
        return;
      }

      const questionData = data.data || data;

      if ((questionData.sequence_number || 1) === 1) {
        void requestInterviewFullscreen();
      }

      console.log('📝 Question received:', data);
      if (questionData.use_video && questionData.video_url) {
        setRaniVideoUrl(questionData.video_url);
      } else {
        setRaniVideoUrl(null);
      }

      ensureQuestionAudioElement();

      console.log('🎵 Audio in questionData:', questionData.audio ? `YES (${questionData.audio.length} chars)` : 'NO');
      console.log('📋 Full questionData:', questionData);

      const applyFirstQuestion = () => {
        if (interviewEndedByUserRef.current) return;
        setCurrentQuestion(questionData);
        currentQuestionIdRef.current = questionData.id;
        currentQuestionSeqRef.current = questionData.sequence_number || 1;
        mainQuestionsShownRef.current = 1; // question 1 is always a real main question
        const seqNum = questionData.sequence_number || 1;
        setQuestionCount(seqNum);
        const tq = questionData.total_questions ?? questionData.totalQuestions;
        setTotalQuestions(tq ?? questionData.total_questions ?? 3);
        console.log(`📊 Question count updated: ${seqNum}/3`);
        if (recordingCountdownRef.current) {
          clearInterval(recordingCountdownRef.current);
          recordingCountdownRef.current = null;
        }
        endAnswerSentRef.current = false;
        processingAckPlayedThisTurnRef.current = false;
        isTurnTimeoutRef.current = false;
        setIsRecording(false);
        setTranscript('');
        transcriptAccumulatorRef.current = '';
        setReadyToSubmit(false);
        console.log('🧹 Cleared transcript accumulator for new question');
        setInterviewStatus('ready');
        setWaitingForAnswer(false);
        setIdleSeconds(0);
        setSystemWorkingNote(null);
        if (idleTimerRef.current) {
          clearInterval(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        // From here the question audio should play and end with
        // beginListeningForAnswer; arm the backstop in case that never lands.
        armPlaybackWatchdog();

        if (videoRecorderRef.current && !videoRecorderRef.current.isRecording) {
          try {
            videoRecorderRef.current.start();
            const startTime = videoRecorderRef.current.getRecordingStartTime();
            setRecordingStartTime(startTime);
            if (socket && connected) {
              socket.emit('recording_started', {
                type: 'recording_started',
                recording_start_time: new Date(startTime).toISOString(),
              });
            }
            if (!livekitRoomRef.current) {
              (async () => {
                try {
                  const accessTok = localStorage.getItem('access_token') || accessToken;
                  const resp = await fetch(`${getPublicApiOrigin()}/api/v1/livekit/candidate-token/${sessionId}`, {
                    headers: { Authorization: accessTok ? `Bearer ${accessTok}` : '' },
                  });
                  if (resp.ok) {
                    const { token, url } = await resp.json();
                    const room = new Room();
                    livekitRoomRef.current = room;
                    await room.connect(url, token);
                    const existingStream =
                      videoRecorderRef.current?.combinedStream ||
                      (videoElementRef.current?.srcObject instanceof MediaStream
                        ? videoElementRef.current.srcObject
                        : null);

                    if (existingStream) {
                      const videoMediaTrack = existingStream.getVideoTracks()[0];
                      const audioMediaTrack = existingStream.getAudioTracks()[0];
                      if (videoMediaTrack) {
                        const { LocalVideoTrack } = await import('livekit-client');
                        const livekitVideo = new LocalVideoTrack(videoMediaTrack);
                        await room.localParticipant.publishTrack(livekitVideo);
                      }
                      if (audioMediaTrack) {
                        const { LocalAudioTrack } = await import('livekit-client');
                        const livekitAudio = new LocalAudioTrack(audioMediaTrack);
                        await room.localParticipant.publishTrack(livekitAudio);
                      }
                    } else {
                      const videoTrack = await createLocalVideoTrack();
                      const audioTrack = await createLocalAudioTrack();
                      await room.localParticipant.publishTrack(videoTrack);
                      await room.localParticipant.publishTrack(audioTrack);
                    }
                    console.log('✅ LiveKit: publishing to monitor');
                  }
                } catch (e) {
                  console.warn('LiveKit publish failed (non-critical):', e);
                }
              })();
            }
          } catch (error) {
            console.error('❌ Failed to start video recording:', error);
          }
        }
      };

      const runFirst = async () => {
        ensureQuestionAudioElement();

        if (questionData.sequence_number === 1) {
          try {
            const candidateFirstName =
              candidateName
                ? candidateName.split(' ')[0].replace(/_/g, ' ').trim()
                : '';
            const introText = candidateFirstName
              ? `Hi ${candidateFirstName}, I'm David. I'll be conducting your interview today. Take your time with each answer, and click Done Answering when you're ready to move on. Let's begin!`
              : `Hi, I'm David. I'll be conducting your interview today. Take your time with each answer, and click Done Answering when you're ready to move on. Let's begin!`;

            const introRes = await fetch(
              `${getPublicApiOrigin()}/api/v1/sessions/${sessionId}/tts`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  text: introText,
                  language: questionData.language || 'en',
                }),
              },
            );
            if (introRes.ok) {
              const audioData = await introRes.json();
              if (audioData.audio) {
                // READY GATE: Ensure models and media pipeline are finished processing
                // before David begins speaking his intro words.
                if (!isObjectDetectionReady()) {
                  setSystemWorkingNote('Connecting to interviewer…');
                  await waitForObjectDetectionReady(4000);
                  setSystemWorkingNote(null);
                }
                lastSpokenTextRef.current += introText + ' ';
                await playTransitionAudio(audioData.audio, introText);
              }
            }
          } catch (e) {
            console.warn('Intro TTS failed:', e);
          }
        }

        const ta = questionData.transition_audio;
        if (ta && questionAudioElementRef.current) {
          lastSpokenTextRef.current += (questionData.transition || '') + ' ';
          const taText = questionData.transition || questionData.transition_text || questionData.ack_text || questionData.acknowledgement;
          if (taText) setDavidSpeechTranscript(taText);
          await playQuestionAudio(ta, questionAudioElementRef.current, interviewEndedByUserRef);
          setDavidSpeechTranscript(null);
        }
        // QuestionDisplay plays questionData.audio itself once currentQuestion
        // is set below — append its text now so it's covered by the same
        // window (its own audio hasn't started yet, but aiSpeaking is still
        // true through this whole turn, same as the transition just played).
        lastSpokenTextRef.current += questionData.question_text || '';
        if (!interviewEndedByUserRef.current) {
          applyFirstQuestion();
        }
      };
      void runFirst();
    };

    const handleTranscription = (data) => {
      console.log('📝 Transcription received:', data);
      setTranscript(data.text || data);
      // Don't automatically end - wait for interview_completed message from backend
    };

    const handleEvaluation = (data) => {
      console.log('📊 Evaluation received:', data);
    };

    const handleNextQuestion = (data) => {
      if (interviewEndedByUserRef.current) {
        console.log('⏭️ Interview already ended — ignoring next_question');
        return;
      }
      console.log('📝 Next question received:', data);
      clearPlaybackWatchdog();
      const nextQuestionData = data.data || data;
      const incomingSeq = nextQuestionData.sequence_number;
      const incomingIsFollowUp = !!nextQuestionData.is_follow_up;
      // Guard: already completed all *main* questions — ignore (prevents Q1
      // re-flash after last answer). Must be main-question count, not raw
      // sequence_number — sequence_number climbs on follow-ups too, so
      // comparing it directly against totalQuestions used to block a
      // genuine next main question the moment enough follow-ups had been
      // inserted (see mainQuestionsShownRef's own comment). A follow-up
      // itself is never blocked here — it's not forward progress past the
      // total, it's still working the current main question.
      if (!incomingIsFollowUp && mainQuestionsShownRef.current >= totalQuestionsRef.current) {
        console.log(`⏭️ Already shown all main questions (${mainQuestionsShownRef.current}/${totalQuestionsRef.current}) — ignoring next_question`);
        return;
      }
      if (incomingSeq && incomingSeq <= currentQuestionSeqRef.current) {
        console.warn(`⚠️ Ignoring backwards next_question seq=${incomingSeq} (current=${currentQuestionSeqRef.current})`);
        return;
      }

      setInterviewStatus('ready');
      setSystemWorkingNote(null);
      setProcessingAnswer(false);
      setWaitingForAnswer(false);

      if (nextQuestionData.audio) {
        console.log(`🎵 Next question has audio (${nextQuestionData.audio.length} chars)`);
      } else {
        console.warn('⚠️ Next question missing audio, requesting fallback TTS...');
        fetch(`${getPublicApiOrigin()}/api/v1/sessions/${sessionId}/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ text: nextQuestionData.question_text }),
        })
        .then((res) => (res.ok ? res.json() : null))
        .then((ttsData) => {
          if (ttsData?.audio) {
            nextQuestionData.audio = ttsData.audio;
            console.log('✅ Fallback TTS received for next question');
          }
        })
        .catch((err) => {
          console.error('❌ Fallback TTS failed for next question:', err);
        });
      }

      if (nextQuestionData.use_video && nextQuestionData.video_url) {
        setRaniVideoUrl(nextQuestionData.video_url);
      } else {
        setRaniVideoUrl(null);
      }

      // Ensure question audio element exists for TTS playback + PiP speaking glow.
      if (!questionAudioElementRef.current) {
        questionAudioElementRef.current = document.createElement('audio');
        questionAudioElementRef.current.crossOrigin = 'anonymous';
        questionAudioElementRef.current.style.display = 'none';
        document.body.appendChild(questionAudioElementRef.current);
        console.log('✅ Created hidden audio element for question (on next question)');
      }
      if (!speakingListenersAttachedRef.current && questionAudioElementRef.current) {
        speakingListenersAttachedRef.current = true;

        const el = questionAudioElementRef.current;
        el.addEventListener('play', () => {
          setIsSpeaking(true);
          setRaniSpeaking(true);
        });
        el.addEventListener('ended', () => {
          setIsSpeaking(false);
          setRaniSpeaking(false);
        });
        el.addEventListener('pause', () => {
          // Only mark as not speaking if the audio actually ended —
          // not if browser paused it (e.g. tab switch)
          if (el.ended || el.currentTime === 0) {
            setIsSpeaking(false);
            setRaniSpeaking(false);
          }
          // If paused but not ended — browser throttled it (tab switch)
          // Keep raniSpeaking true so waitForThankYou doesn't resolve early
        });
      }

      const applyNextQuestion = () => {
        if (interviewEndedByUserRef.current) return;
        setCurrentQuestion(nextQuestionData);
        currentQuestionIdRef.current = nextQuestionData.id;
        currentQuestionSeqRef.current = nextQuestionData.sequence_number;
        // Only a genuine main question advances the "Question X of Y" the
        // candidate sees and counts toward completion — a follow-up keeps
        // showing the main question it's retrying (see mainQuestionsShownRef
        // and the guard in handleNextQuestion for why this must not use
        // raw sequence_number).
        if (!nextQuestionData.is_follow_up) {
          mainQuestionsShownRef.current = nextQuestionData.main_question_number || (mainQuestionsShownRef.current + 1);
        }
        const seqNum = mainQuestionsShownRef.current || 1;
        setQuestionCount(seqNum);
        const tq = nextQuestionData.total_questions ?? nextQuestionData.totalQuestions;
        const resolvedTotal = tq ?? nextQuestionData.total_questions ?? 3;
        setTotalQuestions(resolvedTotal);
        console.log(`📊 Next question count updated: ${seqNum}/${resolvedTotal}`);
        if (recordingCountdownRef.current) {
          clearInterval(recordingCountdownRef.current);
          recordingCountdownRef.current = null;
        }

        if (seqNum > resolvedTotal + resolvedTotal) {
          console.error(`❌ Invalid sequence_number: ${seqNum}, should not exceed 3`);
        }
        setIsRecording(false);
        setTranscript('');
        transcriptAccumulatorRef.current = '';
        setReadyToSubmit(false);
        console.log('🧹 Cleared transcript accumulator for new question');
        setWaitingForAnswer(false);
        setIdleSeconds(0);
        setSystemWorkingNote(null);
        if (idleTimerRef.current) {
          clearInterval(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        setInterviewStatus('ready');
        // Arm the backstop for this question's audio -> beginListeningForAnswer.
        armPlaybackWatchdog();

        if (videoRecorderRef.current && !videoRecorderRef.current.isRecording) {
          console.log('🔄 Restarting video recording for next question...');
          try {
            videoRecorderRef.current.start();
            const startTime = videoRecorderRef.current.getRecordingStartTime();
            setRecordingStartTime(startTime);
            if (socket && connected) {
              socket.emit('recording_started', {
                type: 'recording_started',
                recording_start_time: new Date(startTime).toISOString(),
              });
            }
            if (!livekitRoomRef.current) {
              (async () => {
                try {
                  const accessTok = localStorage.getItem('access_token') || accessToken;
                  const resp = await fetch(`${getPublicApiOrigin()}/api/v1/livekit/candidate-token/${sessionId}`, {
                    headers: { Authorization: accessTok ? `Bearer ${accessTok}` : '' },
                  });
                  if (resp.ok) {
                    const { token, url } = await resp.json();
                    const room = new Room();
                    livekitRoomRef.current = room;
                    await room.connect(url, token);
                    const existingStream =
                      videoRecorderRef.current?.combinedStream ||
                      (videoElementRef.current?.srcObject instanceof MediaStream
                        ? videoElementRef.current.srcObject
                        : null);

                    if (existingStream) {
                      const videoMediaTrack = existingStream.getVideoTracks()[0];
                      const audioMediaTrack = existingStream.getAudioTracks()[0];
                      if (videoMediaTrack) {
                        const { LocalVideoTrack } = await import('livekit-client');
                        const livekitVideo = new LocalVideoTrack(videoMediaTrack);
                        await room.localParticipant.publishTrack(livekitVideo);
                      }
                      if (audioMediaTrack) {
                        const { LocalAudioTrack } = await import('livekit-client');
                        const livekitAudio = new LocalAudioTrack(audioMediaTrack);
                        await room.localParticipant.publishTrack(livekitAudio);
                      }
                    } else {
                      const videoTrack = await createLocalVideoTrack();
                      const audioTrack = await createLocalAudioTrack();
                      await room.localParticipant.publishTrack(videoTrack);
                      await room.localParticipant.publishTrack(audioTrack);
                    }
                    console.log('✅ LiveKit: publishing to monitor');
                  }
                } catch (e) {
                  console.warn('LiveKit publish failed (non-critical):', e);
                }
              })();
            }
          } catch (error) {
            console.error('❌ Failed to restart video recording:', error);
          }
        }
      };

      const runNext = async () => {
        ensureQuestionAudioElement();
        if (!interviewEndedByUserRef.current) {
          // If an acknowledgement audio (e.g. skip_ack, processing_ack) is still playing,
          // wait for it to complete naturally so it is not cut off mid-sentence.
          if (activeAckPlaybackPromiseRef.current) {
            try {
              await activeAckPlaybackPromiseRef.current;
            } catch (_) {
              /* ignore */
            }
          }
          isPlayingProcessingAckRef.current = false;
          setQuestionTransition('out');

          processingAckPlayedThisTurnRef.current = false;

          const ta = nextQuestionData.transition_audio;
          if (ta && questionAudioElementRef.current) {
            lastSpokenTextRef.current += (nextQuestionData.transition || '') + ' ';
            const taText = nextQuestionData.transition || nextQuestionData.transition_text || nextQuestionData.ack_text || nextQuestionData.acknowledgement;
            if (taText) setDavidSpeechTranscript(taText);
            await playQuestionAudio(ta, questionAudioElementRef.current, interviewEndedByUserRef);
            setDavidSpeechTranscript(null);
          } else {
            await new Promise((r) => setTimeout(r, 200));
          }
          // QuestionDisplay plays nextQuestionData.audio itself once
          // applyNextQuestion sets currentQuestion below — same reasoning
          // as runFirst's matching line.
          lastSpokenTextRef.current += nextQuestionData.question_text || '';
          applyNextQuestion();
          setQuestionTransition('in');
        }
      };
      void runNext();
    };

    const handleInterviewCompleted = async (data) => {
      console.log('✅ Interview completed:', data);
      interviewCompletedRef.current = true;
      interviewEndedByUserRef.current = true;

      // Only stop transition audio, don't kill active wrap-up ACK mid-sentence
      const transitionAudio = activeTransitionAudioRef.current;
      if (transitionAudio) {
        try {
          transitionAudio.pause();
          transitionAudio.src = '';
        } catch (_) {}
        activeTransitionAudioRef.current = null;
      }
      clearPlaybackWatchdog();
      setSystemWorkingNote(null);
      setCurrentQuestion(null);
      setQuestionAudioReady(false);
      setRaniVideoUrl(null);
      setProcessingAnswer(false);
      setReadyToSubmit(false);

      setIsRecording(false);
      setWaitingForAnswer(false);
      setIdleSeconds(0);
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      if (recordingCountdownRef.current) {
        clearInterval(recordingCountdownRef.current);
        recordingCountdownRef.current = null;
      }

      try {
        console.log('🎬 Attempting to save video on interview complete...');
        const rec = videoRecorderRef.current;
        // AI + coding: keep the same MediaRecorder running into the coding phase
        // so one continuous SessionVideo is uploaded at coding finish.
        if (expectsCodingPhaseRef.current) {
          console.log('🎬 interview_coding — deferring video upload until coding ends');
        } else if (rec) {
          if (rec.isRecording) {
            console.log('▶️ Recorder is active — calling stopAsync');
            await rec.stopAsync();
          } else if (rec.recordedChunks?.length > 0 && !rec._uploadCompleted) {
            console.log('⚠️ Recorder stopped but has chunks — calling savePartialRecording');
            await rec.savePartialRecording();
          } else {
            console.warn('⚠️ No video data to save');
          }
        } else {
          console.warn('⚠️ videoRecorderRef is null');
        }
      } catch (e) {
        console.error('❌ Video save failed:', e);
        if (!expectsCodingPhaseRef.current) {
          const rec = videoRecorderRef.current;
          if (rec && !rec._uploadCompleted) {
            try {
              await rec.savePartialRecording();
            } catch (e2) {
              console.error('❌ Partial save also failed:', e2);
            }
          }
        }
      }
      if (livekitRoomRef.current) {
        try {
          livekitRoomRef.current.disconnect();
        } catch (e) {
          console.warn('LiveKit disconnect:', e);
        }
        livekitRoomRef.current = null;
      }

      setShowingClosingTTS(true);
      setClosingTtsPreparing(true);
      setRaniSpeaking(true);
      try {
        ensureQuestionAudioElement();
        const qaEl = questionAudioElementRef.current;
        const onClosingPlay = () => setClosingTtsPreparing(false);
        if (qaEl) {
          qaEl.addEventListener('play', onClosingPlay, { once: true });
        }
        const prefetch = closingTtsPromiseRef.current;
        closingTtsPromiseRef.current = null;
        const ttsFetch = prefetch || prefetchClosingTts();
        let audioData = null;
        if (ttsFetch) {
          const response = await ttsFetch;
          if (response.ok) {
            audioData = await response.json();
          }
        }
        if (audioData?.audio && qaEl) {
          // If wrap-up ACK audio ("Alright, let me wrap up this session for you") is still playing,
          // wait for it to complete naturally before David speaks the final closing message.
          if (activeAckPlaybackPromiseRef.current) {
            try {
              await activeAckPlaybackPromiseRef.current;
            } catch (_) {}
          }
          isPlayingProcessingAckRef.current = false;

          setDavidSpeechTranscript(CLOSING_MESSAGE);
          await playClosingTtsBlob(audioData.audio, qaEl);
          setDavidSpeechTranscript(null);
        }
      } catch (e) {
        console.warn('Closing TTS failed:', e);
      } finally {
        setDavidSpeechTranscript(null);
      }
      setRaniSpeaking(false);
      setShowingClosingTTS(false);
      setClosingTtsPreparing(false);

      // Combined AI+coding: keep media / tab lock / fullscreen for coding phase.
      if (expectsCodingPhase && typeof onContinueToCoding === 'function') {
        intentionalExitRef.current = true;
        setInterviewStatus('completed');
        setShowGoodbye(true);
        if (socket) {
          console.log('🔌 Closing WebSocket after interview phase (coding next)');
          socket.disconnect();
        }
        return;
      }

      // Keep camera/mic alive through closing TTS — release only after playback ends.
      releaseAllMediaStreams();

      localStorage.removeItem('wiztok_active_session');
      sessionStorage.removeItem('wiztok_owner_tab');
      try {
        const channel = new BroadcastChannel('wiztok_interview');
        channel.postMessage({ type: 'session_ended' });
        channel.close();
      } catch (e) {
        console.warn('BroadcastChannel session_ended failed:', e);
      }

      setInterviewStatus('completed');
      setShowGoodbye(true);

      intentionalExitRef.current = true;
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      }

      if (socket) {
        console.log('🔌 Closing WebSocket connection immediately');
        socket.disconnect();
      }
    };

    const handleWarning = (data) => {
      console.warn('⚠️ Warning received:', data);
      showNotification(data.message || data.type || 'Warning');
    };

    // Step 4 intent-layer replies. The server classifies "can you repeat
    // the question" / "give me a second" / etc. before scoring and stays
    // on the same question (no next_question follows) — so the frontend
    // has to clear processingAnswer and let the candidate answer again
    // itself, or "Processing your answer..." hangs forever. That hang,
    // not a classification miss, is what "still not working" turned out
    // to be: the server was replying correctly, nothing here read the reply.
    const resetAnswerStateForRetry = () => {
      endAnswerSentRef.current = false;
      processingAckPlayedThisTurnRef.current = false;
      isTurnTimeoutRef.current = false;
      setIsRecording(false);
      setTranscript('');
      transcriptAccumulatorRef.current = '';
      setReadyToSubmit(false);
      setWaitingForAnswer(false);
      setIdleSeconds(0);
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const handleSameQuestionRetry = (data) => {
      if (interviewEndedByUserRef.current) return;
      const payload = data.data || data;
      console.log(`🧭 ${data.type}:`, payload);
      setProcessingAnswer(false);
      resetAnswerStateForRetry();
      setSystemWorkingNote(null);
      // Suppress red warning toast for repeat_question (normal conversational retry)
      if (payload.message && data.type !== 'repeat_question') {
        showNotification(payload.message);
      }
      ensureQuestionAudioElement();
      // See lastSpokenTextRef's own comment on why this appends rather
      // than resets.
      lastSpokenTextRef.current += (payload.message || '') + ' ' + (payload.question_text || '') + ' ';
      // This path replays audio and then calls beginListeningForAnswer in a
      // .then() — same stall risk as a fresh question, so back it the same way.
      armPlaybackWatchdog();

      // Must resume listening after the replayed audio ends — this was
      // the actual bug: nothing here ever re-armed the mic, so repeating
      // a question left the candidate stuck with no way to answer.
      const playMainThenListen = () => {
        if (payload.audio && questionAudioElementRef.current) {
          setRaniSpeaking(true);
          playQuestionAudio(
            payload.audio,
            questionAudioElementRef.current,
            interviewEndedByUserRef,
            payload.playback_rate || 1,
          ).then(() => {
            if (!interviewEndedByUserRef.current) beginListeningForAnswer();
          }).catch(() => {
            if (!interviewEndedByUserRef.current) beginListeningForAnswer();
          });
        } else if (!interviewEndedByUserRef.current) {
          // No audio to play (e.g. cached audio missing) — nothing to wait
          // on, so resume immediately rather than stalling with no audio
          // playing and no mic listening.
          beginListeningForAnswer();
        }
      };

      // Spoken acknowledgement (pre-cached, see intent_responses.py) plays
      // first — the candidate hears an immediate reply to what they
      // actually asked, then the real response (replayed question, next
      // question, etc.). Only repeat_question currently carries one;
      // absent for clarification/off_topic_reply/declined, which fall
      // straight through to the main audio as before.
      const ackMsg = payload.ack_text || payload.message;
      if (payload.ack_audio && questionAudioElementRef.current) {
        setRaniSpeaking(true);
        if (ackMsg) setDavidSpeechTranscript(ackMsg);
        const p = playQuestionAudio(payload.ack_audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
          setDavidSpeechTranscript(null);
          if (activeAckPlaybackPromiseRef.current === p) {
            activeAckPlaybackPromiseRef.current = null;
          }
          if (!interviewEndedByUserRef.current) playMainThenListen();
        }).catch(() => {
          setDavidSpeechTranscript(null);
          if (activeAckPlaybackPromiseRef.current === p) {
            activeAckPlaybackPromiseRef.current = null;
          }
          if (!interviewEndedByUserRef.current) playMainThenListen();
        });
        activeAckPlaybackPromiseRef.current = p;
      } else {
        playMainThenListen();
      }
    };

    // Advisory-only replies — the server's own next_question or
    // interview_completed (sent right after these) does the real state
    // transition; just surface the message and make sure processing
    // doesn't hang if that follow-up message is delayed.
    const handleAdvisory = (data) => {
      if (interviewEndedByUserRef.current) return;
      const payload = data.data || data;
      const message = payload.message || data.message;
      console.log(`🧭 ${data.type}:`, message);
      setProcessingAnswer(false);
      // Suppress red warning toast for skip_ack (normal conversational skip)
      if (message && data.type !== 'skip_ack') {
        showNotification(message);
      }

      const shouldReArmListening = data.type === 'silence_recovery' || data.type === 'silence_nudge';

      // Spoken acknowledgement (e.g. silence_nudge, silence_recovery, skip_ack)
      if (payload.audio) {
        ensureQuestionAudioElement();
        if (questionAudioElementRef.current) {
          setRaniSpeaking(true);
          if (message) lastSpokenTextRef.current += message + ' ';
          if (message) setDavidSpeechTranscript(message);
          const p = playQuestionAudio(payload.audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
            setRaniSpeaking(false);
            setDavidSpeechTranscript(null);
            if (activeAckPlaybackPromiseRef.current === p) {
              activeAckPlaybackPromiseRef.current = null;
            }
            if (shouldReArmListening && !interviewEndedByUserRef.current) {
              beginListeningForAnswer();
            }
          }).catch(() => {
            setRaniSpeaking(false);
            setDavidSpeechTranscript(null);
            if (activeAckPlaybackPromiseRef.current === p) {
              activeAckPlaybackPromiseRef.current = null;
            }
            if (shouldReArmListening && !interviewEndedByUserRef.current) {
              beginListeningForAnswer();
            }
          });
          activeAckPlaybackPromiseRef.current = p;
        }
      } else if (shouldReArmListening && !interviewEndedByUserRef.current) {
        beginListeningForAnswer();
      }
    };

    // Sent the instant a real answer starts evaluate()/decide() (1-3s+),
    // before next_question/transition_audio exist to send yet. Plays
    // in parallel with thinking without delay, showing word-by-word subtitles above David.
    const handleProcessingAck = (data) => {
      if (interviewEndedByUserRef.current) return;
      if (processingAckPlayedThisTurnRef.current) {
        console.log('⏭️ Instant thinking ACK already played/playing this turn — ignoring server processing_ack');
        return;
      }
      const payload = data.data || data;
      const ackText = payload.message || payload.text;
      processingAckPlayedThisTurnRef.current = true;
      if (payload.audio) {
        ensureQuestionAudioElement();
        if (questionAudioElementRef.current) {
          if (ackText) lastSpokenTextRef.current += ackText + ' ';
          isPlayingProcessingAckRef.current = true;
          if (ackText) setDavidSpeechTranscript(ackText);
          const p = playQuestionAudio(payload.audio, questionAudioElementRef.current, interviewEndedByUserRef).then(() => {
            isPlayingProcessingAckRef.current = false;
            setDavidSpeechTranscript(null);
            if (activeAckPlaybackPromiseRef.current === p) {
              activeAckPlaybackPromiseRef.current = null;
            }
          }).catch(() => {
            isPlayingProcessingAckRef.current = false;
            setDavidSpeechTranscript(null);
            if (activeAckPlaybackPromiseRef.current === p) {
              activeAckPlaybackPromiseRef.current = null;
            }
          });
          activeAckPlaybackPromiseRef.current = p;
        }
      }
    };

    // The backend is still in ASKING waiting for a client confirmation
    // (playback_ended today) that hasn't arrived. Purely time-driven on the
    // server; nothing about this turn is special. Deliberately visual-only
    // and no audio — the question audio may still be playing, and this must
    // never talk over it. Persistent (not showNotification's 3s toast) so it
    // is still visible if the candidate looks up later; cleared on any real
    // progression (beginListeningForAnswer / next question / completion).
    const handleSystemWorking = (data) => {
      if (interviewEndedByUserRef.current || interviewCompletedRef.current) return;
      const payload = data.data || data;
      const message = payload.message || 'Still working…';
      console.log('⏳ system_working:', message);
      setSystemWorkingNote(message);
    };

    // Register event handlers
    socket.on('connected', handleConnected);
    socket.on('error', handleError);
    socket.on('disconnect', handleDisconnect);
    socket.on('question', handleQuestion);
    socket.on('transcription', handleTranscription);
    socket.on('evaluation', handleEvaluation);
    socket.on('next_question', handleNextQuestion);
    socket.on('interview_completed', handleInterviewCompleted);
    socket.on('warning', handleWarning);
    socket.on('repeat_question', handleSameQuestionRetry);
    socket.on('clarification', handleSameQuestionRetry);
    socket.on('needs_time', handleSameQuestionRetry);
    socket.on('technical_problem', handleSameQuestionRetry);
    socket.on('off_topic_reply', handleSameQuestionRetry);
    socket.on('declined', handleSameQuestionRetry);
    socket.on('processing_ack', handleProcessingAck);
    socket.on('skip_ack', handleAdvisory);
    socket.on('prompt_injection_blocked', handleAdvisory);
    socket.on('abuse_warning', handleAdvisory);
    socket.on('silence_nudge', handleAdvisory);
    socket.on('silence_recovery', handleAdvisory);
    socket.on('moderation_warning', handleAdvisory);
    socket.on('system_working', handleSystemWorking);

    return () => {
      if (socket) {
        socket.off('connected', handleConnected);
        socket.off('error', handleError);
        socket.off('disconnect', handleDisconnect);
        socket.off('question', handleQuestion);
        socket.off('transcription', handleTranscription);
        socket.off('evaluation', handleEvaluation);
        socket.off('next_question', handleNextQuestion);
        socket.off('interview_completed', handleInterviewCompleted);
        socket.off('warning', handleWarning);
        socket.off('repeat_question', handleSameQuestionRetry);
        socket.off('clarification', handleSameQuestionRetry);
        socket.off('needs_time', handleSameQuestionRetry);
        socket.off('technical_problem', handleSameQuestionRetry);
        socket.off('off_topic_reply', handleSameQuestionRetry);
        socket.off('declined', handleSameQuestionRetry);
        socket.off('processing_ack', handleProcessingAck);
        socket.off('skip_ack', handleAdvisory);
        socket.off('prompt_injection_blocked', handleAdvisory);
        socket.off('abuse_warning', handleAdvisory);
        socket.off('silence_nudge', handleAdvisory);
        socket.off('silence_recovery', handleAdvisory);
        socket.off('moderation_warning', handleAdvisory);
        socket.off('system_working', handleSystemWorking);
      }
    };
  }, [socket, connected, sessionId, accessToken, navigate, showNotification, ensureQuestionAudioElement, stopAllInterviewAudio, prefetchClosingTts, releaseAllMediaStreams, requestInterviewFullscreen, expectsCodingPhase, onContinueToCoding]);

  const handleStartRecording = useCallback(() => {
    setIsRecording(true);
    setTranscript('');
  }, []);

  const handleTranscript = useCallback(
    (text, isFinal) => {
      if (text && String(text).trim()) {
        setIdleSeconds(0);
      }
      if (endAnswerSentRef.current) {
        console.log('⚠️ Ignoring transcript — end_answer already sent for this question');
        return;
      }

      if (
        transcriptQuestionIdRef.current != null &&
        currentQuestionIdRef.current != null &&
        transcriptQuestionIdRef.current !== currentQuestionIdRef.current
      ) {
        console.warn('Ignoring stale transcript for previous question');
        return;
      }

      // Ignore transcript if it belongs to a different question
      if (
        answerQuestionIdRef.current &&
        currentQuestionIdRef.current &&
        answerQuestionIdRef.current !== currentQuestionIdRef.current
      ) {
        console.log('⚠️ Ignoring late transcript from previous question');
        return;
      }

      if (interviewStatus === 'completed' || showGoodbye) {
        console.log('⚠️ Interview completed, ignoring transcript');
        return;
      }

      // Speech while AI is speaking — ignored (barge-in unplugged to prevent mid-question cutoff and audio bleed)
      if (
        !waitingForAnswerRef.current &&
        (raniSpeaking || isSpeaking)
      ) {
        return;
      }

      // Use ref (always current) instead of state captured in closure — prevents
      // stale useCallback snapshot from dropping valid transcripts when React
      // hasn't re-rendered yet after waitingForAnswer became true.
      if (!waitingForAnswerRef.current) {
        console.log('⚠️ Ignoring transcript (not in answer window — avoids stale chunks after submit)');
        return;
      }

      if (!text || !text.trim()) {
        console.log('⚠️ Empty transcript, ignoring');
        return;
      }

      if (socket && connected) {
        if (isFinal) {
          console.log(`📤 Sending final transcript chunk:`, text);

          // Does NOT write transcriptAccumulatorRef — that's the single
          // value handleEndAnswer submits, and VoiceRecorder's onStop
          // (handleVoiceRecorderStop) is its one authoritative writer.
          // This used to also append here, so any final result that
          // arrived before silence-detection fired got written twice —
          // once here, once again via onStop's own complete capture,
          // producing the literal repeated text a real answer surfaced.
          // The server-side transcript_chunk send below is unaffected —
          // the backend's own transcript_chunks buffer (used by the
          // SILENCE watchdog fallback) is a separate concern.
          socket.emit('transcript_chunk', {
            type: 'transcript_chunk',
            text: text.trim(),
            is_final: true,
            timestamp: Date.now(),
            question_seq: currentQuestionSeqRef.current,
          });

          setTranscript(text.trim());
        } else {
          // Interim (in-progress) result. NOT answer content — interim text
          // is revised/replaced before it finalizes, so it never touches
          // transcriptAccumulatorRef or the transcript_chunk buffer the
          // server scores. Its only meaning is "the candidate is still
          // talking". Emit a distinct, content-free keepalive so the server
          // resets its silence clock (last_activity_at) instead of firing a
          // silence_nudge over someone who is mid-answer but between final
          // chunks. Throttled — interim events can arrive many times per
          // second and must not flood the socket. Payload deliberately
          // carries no text; question_seq only lets a future server drop
          // stale pings, mirroring transcript_chunk.
          const now = Date.now();
          if (now - lastActivityPingAtRef.current >= ACTIVITY_PING_MIN_INTERVAL_MS) {
            lastActivityPingAtRef.current = now;
            socket.emit('transcript_activity', {
              type: 'transcript_activity',
              timestamp: now,
              question_seq: currentQuestionSeqRef.current,
            });
            console.log('📶 transcript_activity keepalive sent (interim, no content)');
          } else {
            console.log(`📝 Interim transcript (throttled, not sending):`, text);
          }
        }
      } else {
        console.warn('⚠️ Cannot send transcript - socket not connected');
      }
    },
    [socket, connected, interviewStatus, showGoodbye, currentQuestion, showingClosingTTS, questionCount],
  );

  const handleEndAnswer = useCallback(async (transcriptOverride, options = {}) => {
    if (interviewEndedByUserRef.current || interviewStatus === 'completed' || showGoodbye) {
      return;
    }
    if (endAnswerSentRef.current) {
      console.log('⏭️ end_answer already sent for this question — ignoring duplicate');
      return;
    }
    if (!socket || !connected) {
      console.warn('⚠️ Cannot send end_answer - socket not connected');
      return;
    }

    setProcessingAnswer(true);

    let finalTranscript = (
      transcriptOverride !== undefined ? String(transcriptOverride) : (transcriptAccumulatorRef.current || '')
    ).trim();

    const isTimeoutAnswer = Boolean(options.isTimeout || isTurnTimeoutRef.current);
    isTurnTimeoutRef.current = false;

    // 1. Time-Limit Timeout (2 minutes expired): Candidate spoke up to the max duration.
    // Play dedicated timeout acknowledgment ("That's time for this question...", etc.)
    if (isTimeoutAnswer) {
      playInstantTimeoutAck();
      console.log('🎙️ Played instant timeout ACK (2-minute limit reached) for transcript:', finalTranscript || '(empty)');
    }
    // 2. End-Interview Intent: Candidate verbally requested to end/wrap up the interview
    else if (isVoiceEndInterviewUtterance(finalTranscript)) {
      playInstantWrapUpAck();
      console.log('🎙️ Played instant wrap-up ACK for end-interview intent in transcript:', finalTranscript);
    }
    // 3. Spoken candidate answer: Play standard anti-repetition thinking filler (only if words were spoken)
    else if (finalTranscript) {
      // INSTANT THINKING FILLER (<15ms): David speaks immediately from pre-warmed anti-repetition pool
      playInstantThinkingAck();
    } else {
      console.log('⏭️ Transcript is empty (no words spoken) — skipping instant thinking ACK');
    }

    endAnswerSentRef.current = true;

    if (recordingCountdownRef.current) {
      clearInterval(recordingCountdownRef.current);
      recordingCountdownRef.current = null;
    }
    setRecordingSecondsLeft(null);
    if (idleTimerRef.current) {
      clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setIdleSeconds(0);

    setIsRecording(false);
    setWaitingForAnswer(false);

    // Fast check: If live transcript has words, submit immediately (0ms wait).
    // Only if completely empty (e.g. STT catching up), give Whisper a fast race window.
    const wordCount = finalTranscript ? finalTranscript.split(/\s+/).filter(Boolean).length : 0;
    if (wordCount === 0) {
      console.log(`⏳ Live transcript empty (0 words) — checking Whisper (up to 800ms race)...`);
      const whisperResult = await Promise.race([
        new Promise((resolve) => {
          whisperPendingRef.current = { resolve };
        }),
        new Promise((resolve) => setTimeout(() => resolve(null), 800)),
      ]);
      whisperPendingRef.current = null;
      if (whisperResult && whisperResult.trim().length > finalTranscript.length) {
        console.log('✅ Whisper provided fuller transcript:', whisperResult);
        finalTranscript = whisperResult.trim();
      }
    }

    console.log('📤 Sending end_answer with transcript:', finalTranscript || '(empty)');

    socket.emit('end_answer', {
      type: 'end_answer',
      transcript: finalTranscript,
    });

    transcriptAccumulatorRef.current = '';
    currentQuestionIdRef.current = null;
  }, [
    socket,
    connected,
    interviewStatus,
    showGoodbye,
    questionCount,
    totalQuestions,
    candidateName,
    currentQuestion,
    sessionId,
    playTransitionAudio,
    playInstantThinkingAck,
    playInstantWrapUpAck,
    playInstantTimeoutAck,
  ]);

  useEffect(() => {
    handleEndAnswerRef.current = handleEndAnswer;
  }, [handleEndAnswer]);

  const handleAudioBlob = useCallback(
    async (blob) => {
      if (!sessionId || !accessToken) return;
      if (interviewStatus === 'completed' || showGoodbye) return;
      setIsWhisperLoading(true);
      try {
        const formData = new FormData();
        formData.append('audio', blob, 'answer.webm');
        formData.append('session_id', sessionId);
        formData.append('access_token', accessToken);
        formData.append('language', currentQuestion?.language || 'en');
        const res = await fetch(`${getPublicApiOrigin()}/api/v1/public/transcribe`, {
          method: 'POST',
          body: formData,
        });
        let whisperText = null;
        if (res.ok) {
          const data = await res.json();
          if (data.text && data.text.trim()) {
            whisperText = data.text.trim();
            // This call is async and arrives on its own schedule — often
            // during the NEXT turn's recording window, since Whisper
            // frequently returns after the fast browser transcript has
            // already been submitted. It never overwrites
            // transcriptAccumulatorRef for that reason — it used to,
            // gated only by endAnswerSentRef (reset for the next turn
            // before a stale previous-turn response arrived), silently
            // re-contaminating a freshly-cleared accumulator. Per the
            // original design (Step 2): the fast transcript drives the
            // live conversation. The one place Whisper's result IS used
            // is handleEndAnswer's own weak-transcript wait below (via
            // whisperPendingRef) — a live decision about THIS turn's
            // answer, not a retroactive rewrite of a accumulator that may
            // already belong to a different turn.
            console.log('✅ Whisper transcript received:', whisperText.slice(0, 80));
          }
        } else {
          console.warn('⚠️ Whisper endpoint returned', res.status, '— keeping STT transcript');
        }
        if (whisperPendingRef.current) {
          whisperPendingRef.current.resolve(whisperText);
          whisperPendingRef.current = null;
        }
      } catch (e) {
        console.warn('⚠️ Whisper transcription failed, keeping STT transcript:', e.message);
        if (whisperPendingRef.current) {
          whisperPendingRef.current.resolve(null);
          whisperPendingRef.current = null;
        }
      } finally {
        setIsWhisperLoading(false);
      }
    },
    [sessionId, accessToken, currentQuestion, interviewStatus, showGoodbye],
  );

  const handleVoiceRecorderStop = useCallback(
    (finalTranscript) => {
      if (enteringReviewRef.current) return;
      console.log('🛑 Recording stopped, final transcript:', finalTranscript);
      if (recordingCountdownRef.current) {
        clearInterval(recordingCountdownRef.current);
        recordingCountdownRef.current = null;
      }
      if (finalTranscript && String(finalTranscript).trim()) {
        if (
          transcriptQuestionIdRef.current != null &&
          currentQuestionIdRef.current != null &&
          transcriptQuestionIdRef.current !== currentQuestionIdRef.current
        ) {
          console.warn('Ignoring stale transcript for previous question');
        } else {
          // VoiceRecorder's own capture (its fullTranscriptRef +
          // pendingInterimRef, waited-for via recognition.onend before
          // this fires) is the complete, authoritative text for this
          // turn on its own — REPLACE, not append. transcriptAccumulatorRef
          // has no other writer now (handleTranscript no longer touches
          // it, and Whisper's handleAudioBlob doesn't either — see there
          // for why), so there's nothing legitimate left to preserve by
          // appending to.
          transcriptAccumulatorRef.current = String(finalTranscript).trim();
          setTranscript(transcriptAccumulatorRef.current);
        }
      }
      handleEndAnswer();
    },
    [handleEndAnswer],
  );

  // Step 5 — turn detection. Stopping without setting enteringReviewRef
  // lets the existing isRecording->onStop->handleVoiceRecorderStop chain
  // run its normal (non-review) path straight into handleEndAnswer — the
  // same "auto-submit on stop" pathway the manual button used to skip by
  // setting that ref first. Both the silence timer and the manual "I'm
  // done" button now go through this, so turn detection removes the
  // record/stop/submit round-trip without adding a second code path.
  const handleSilenceDetected = useCallback(() => {
    if (endAnswerSentRef.current) return;
    console.log('🔇 Turn detection: silence after speech, auto-submitting');
    if (recordingCountdownRef.current) {
      clearInterval(recordingCountdownRef.current);
      recordingCountdownRef.current = null;
    }
    if (idleTimerRef.current) {
      clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setIdleSeconds(0);
    setRecordingSecondsLeft(null);
    setIsRecording(false);
    setWaitingForAnswer(false);
    // Real production repro found this: VoiceRecorder's render condition
    // below is `(waitingForAnswer && isRecording) || readyToSubmit` —
    // flipping isRecording/waitingForAnswer straight to false, without
    // this, unmounted VoiceRecorder in the SAME render, tearing it down
    // before its own stop sequence (which waits for the browser's
    // trailing final speech result) could ever call onStop. The
    // interview only ever progressed because the server's own 60s
    // SILENCE watchdog eventually forced an empty answer through
    // instead — the candidate's real, spoken answer never reached the
    // backend at all. readyToSubmit keeps VoiceRecorder mounted long
    // enough to finish and report back; it's cleared automatically once
    // handleEndAnswer flips processingAnswer true, or when the next
    // question arrives.
    setReadyToSubmit(true);
  }, []);

  // Client-side mirror of the server's ASKING timeout. The question audio's
  // own 'ended' event (QuestionDisplay -> onQuestionComplete) is what calls
  // beginListeningForAnswer and emits playback_ended. If that chain never
  // completes — play() rejected by autoplay policy, 'ended' throttled away
  // while the tab is backgrounded, the audio element torn down mid-play, the
  // emit dropped on a reconnecting socket — the turn stalls with no mic and
  // the server never learns playback finished. This backstop forces the same
  // transition anyway so the candidate can still answer (the question text
  // is on screen). Generic: keyed to nothing about a specific question,
  // audio file, browser, or cause. Fires below the server's 60s net so the
  // client recovers first (the server then gets a real playback_ended rather
  // than falling back to "assume finished"); set above any plausible
  // transition + question playback so a healthy turn never trips it.
  const beginListeningForAnswerRef = useRef(() => {});
  const playbackWatchdogRef = useRef(null);
  const PLAYBACK_WATCHDOG_MS = 45000;
  const clearPlaybackWatchdog = useCallback(() => {
    if (playbackWatchdogRef.current) {
      clearTimeout(playbackWatchdogRef.current);
      playbackWatchdogRef.current = null;
    }
  }, []);
  const armPlaybackWatchdog = useCallback(() => {
    clearPlaybackWatchdog();
    playbackWatchdogRef.current = setTimeout(() => {
      playbackWatchdogRef.current = null;
      if (interviewEndedByUserRef.current || interviewCompletedRef.current) return;
      // Already moved past the "waiting for playback" phase — nothing to do.
      if (endAnswerSentRef.current || waitingForAnswerRef.current) return;
      console.warn('⏱️ Question audio never reported complete — starting listening anyway');
      beginListeningForAnswerRef.current();
    }, PLAYBACK_WATCHDOG_MS);
  }, [clearPlaybackWatchdog]);

  useEffect(() => clearPlaybackWatchdog, [clearPlaybackWatchdog]);

  // Shared by the normal question flow (QuestionDisplay's onQuestionComplete,
  // fired when a freshly-asked question's audio ends) and repeat/clarify/
  // needs_time/technical_problem/off_topic_reply/declined (handleSameQuestionRetry
  // below, fired when the REPLAYED audio ends) — both need to start listening
  // again afterward. Before this, only the normal flow did: repeating a
  // question replayed the audio and then just sat there, mic never
  // restarted, because nothing after playQuestionAudio() in
  // handleSameQuestionRetry ever re-armed listening.
  const beginListeningForAnswer = useCallback(() => {
    setRaniSpeaking(false);
    // Playback phase is over (however we got here) — stand down the backstop
    // and drop any "still working" status the server put up while waiting.
    clearPlaybackWatchdog();
    setSystemWorkingNote(null);
    // Tell the server the candidate is actually free to answer now — it
    // was starting its silence-nudge/move-on clock the instant it SENT a
    // question/repeat/transition, not when this ever actually finished
    // playing here, so long intro+question audio ate into the candidate's
    // real thinking time and could fire "are you there?" (or even move on)
    // while the bot was still mid-sentence. The server now waits for this
    // signal (with its own timeout fallback) before starting that clock.
    try {
      socketRef.current?.emit?.('playback_ended', {});
    } catch (_) { /* best-effort — server's own fallback timeout covers a lost signal */ }
    if (recordingCountdownRef.current) {
      clearInterval(recordingCountdownRef.current);
      recordingCountdownRef.current = null;
    }
    endAnswerSentRef.current = false;
    processingAckPlayedThisTurnRef.current = false;
    isTurnTimeoutRef.current = false;
    transcriptAccumulatorRef.current = '';
    setTranscript('');
    setReadyToSubmit(false);
    answerQuestionIdRef.current = currentQuestionIdRef.current;
    transcriptQuestionIdRef.current = currentQuestionIdRef.current;
    setWaitingForAnswer(true);
    setIsRecording(true);
    setIdleSeconds(0);
    if (idleTimerRef.current) {
      clearInterval(idleTimerRef.current);
    }
    idleTimerRef.current = setInterval(() => {
      setIdleSeconds((prev) => prev + 1);
    }, 1000);
    // 2-minute max recording time (count up from 0)
    const MAX_RECORDING_SECONDS = 120;
    let secsElapsed = 0;
    setRecordingSecondsLeft(secsElapsed);
    recordingCountdownRef.current = setInterval(() => {
      secsElapsed += 1;
      setRecordingSecondsLeft(secsElapsed);
      if (secsElapsed >= MAX_RECORDING_SECONDS) {
        clearInterval(recordingCountdownRef.current);
        recordingCountdownRef.current = null;
        setRecordingSecondsLeft(null);
        isTurnTimeoutRef.current = true;
        console.log('⏱️ 2-minute answer time limit reached — stopping recording and submitting full transcript');
        handleSilenceDetected();
      }
    }, 1000);
  }, [clearPlaybackWatchdog, handleSilenceDetected]);

  useEffect(() => {
    beginListeningForAnswerRef.current = beginListeningForAnswer;
  }, [beginListeningForAnswer]);

  // Real-time Dictation Interruption Listener (Barge-In) — UNPLUGGED to prevent audio bleed and mid-speech cutoffs
  const dictationRecognitionRef = useRef(null);

  useEffect(() => {
    // Unplugged: do not listen during AI dictation
    if (dictationRecognitionRef.current) {
      try {
        dictationRecognitionRef.current.abort();
      } catch (_) {}
      dictationRecognitionRef.current = null;
    }
    return undefined;
  }, []);

  const handleEndInterviewClick = () => {
    if (endingInterviewRef.current || isEndingInterview || interviewStatus === 'completed') return;

    if (!socket || !connected) {
      showNotification('Not connected to interview session. If you ended the interview, your recording may still be uploading.');
      return;
    }

    setShowEndConfirmModal(true);
  };

  const handleConfirmEndInterview = async () => {
    setShowEndConfirmModal(false);
    if (endingInterviewRef.current) return;

    if (!socket || !connected) {
      showNotification('Not connected to interview session. If you ended the interview, your recording may still be uploading.');
      return;
    }

    stopAllInterviewAudio();
    clearPlaybackWatchdog();
    setSystemWorkingNote(null);
    endingInterviewRef.current = true;
    setIsEndingInterview(true);

    interviewCompletedRef.current = true;
    interviewEndedByUserRef.current = true;

    closingTtsPromiseRef.current = prefetchClosingTts();

    setCurrentQuestion(null);
    setQuestionAudioReady(false);
    setRaniVideoUrl(null);
    setProcessingAnswer(false);
    setReadyToSubmit(false);

    if (recordingCountdownRef.current) {
      clearInterval(recordingCountdownRef.current);
      recordingCountdownRef.current = null;
    }
    if (idleTimerRef.current) {
      clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    setIdleSeconds(0);
    setIsRecording(false);
    setWaitingForAnswer(false);

    if (socket && connected) {
      socket.emit('end_interview', {
        type: 'end_interview',
        session_id: sessionId,
      });
    }
  };

  const applyMediaControlTracks = useCallback((micOn, camOn) => {
    const prepStream = prepResources?.stream || null;
    prepStream?.getAudioTracks?.().forEach((t) => {
      t.enabled = micOn;
    });
    prepStream?.getVideoTracks?.().forEach((t) => {
      t.enabled = camOn;
    });

    const recorder = videoRecorderRef.current;
    // Mute the raw mic feed (keeps question TTS in the mixed recording)
    if (recorder?.micStream) {
      recorder.micStream.getAudioTracks?.().forEach((t) => {
        t.enabled = micOn;
      });
    } else {
      recorder?.combinedStream?.getAudioTracks?.().forEach((t) => {
        t.enabled = micOn;
      });
    }
    recorder?.combinedStream?.getVideoTracks?.().forEach((t) => {
      t.enabled = camOn;
    });

    // PiP / camera element may use the same live stream object
    const live =
      recorder?.combinedStream ||
      prepStream ||
      null;
    live?.getVideoTracks?.().forEach((t) => {
      t.enabled = camOn;
    });
  }, [prepResources]);

  const toggleControlMic = useCallback(() => {
    setControlMicOn((prev) => {
      const next = !prev;
      applyMediaControlTracks(next, controlCamOn);
      return next;
    });
  }, [applyMediaControlTracks, controlCamOn]);

  const toggleControlCam = useCallback(() => {
    setControlCamOn((prev) => {
      const next = !prev;
      applyMediaControlTracks(controlMicOn, next);
      return next;
    });
  }, [applyMediaControlTracks, controlMicOn]);

  const handleControlScreenShare = useCallback(async () => {
    if (!showScreenShareGate && isStreamLive(screenStreamRef.current)) {
      showNotification('Screen sharing is already active');
      return;
    }
    await handleRestoreScreenShare();
  }, [handleRestoreScreenShare, showNotification, showScreenShareGate]);

  // Re-apply mute/camera if recorder/prep streams appear after a toggle
  useEffect(() => {
    applyMediaControlTracks(controlMicOn, controlCamOn);
  }, [applyMediaControlTracks, controlMicOn, controlCamOn, recordingStartTime]);

  useEffect(() => {
    const flushVideo = () => {
      const r = videoRecorderRef.current;
      if (!r || r._uploadCompleted) return;
      if (typeof r.stopAsync === 'function') {
        void r.stopAsync().catch(() => {});
      } else if (typeof r.savePartialRecording === 'function') {
        void r.savePartialRecording();
      } else {
        r.stop();
      }
    };
    window.addEventListener('pagehide', flushVideo);
    window.addEventListener('beforeunload', flushVideo);
    return () => {
      window.removeEventListener('pagehide', flushVideo);
      window.removeEventListener('beforeunload', flushVideo);
    };
  }, [sessionId]);

  // If no access token, check if we have session data from registration
  useEffect(() => {
    if (!accessToken && sessionId) {
      const sessionData = localStorage.getItem('candidate_session');
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          if (parsed.session_id === sessionId && parsed.access_token) {
            localStorage.setItem('access_token', parsed.access_token);
            window.location.reload();
          }
        } catch (e) {
          console.error('Error parsing session data:', e);
        }
      }
    }
  }, [accessToken, sessionId]);
  
  // Only show error if NOT from landing page AND no token found
  if (!accessToken && !isFromLanding) {
    // Check one more time if token exists
    const token = localStorage.getItem('access_token');
    if (!token) {
      return (
        <div className="interview-room">
          <div className="error-state">
            <h2>Please use the position link to start the interview</h2>
            <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Go back to the campaign registration page to complete the flow.
            </p>
          </div>
        </div>
      );
    }
  }
  
  // If from landing but no token yet, wait briefly
  if (!accessToken && isFromLanding) {
    return (
      <div className="interview-room">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Preparing interview...</p>
        </div>
      </div>
    );
  }

  if (interviewStatus === 'loading') {
    return (
      <div className="interview-room">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Connecting to interview...</p>
        </div>
      </div>
    );
  }

  if (interviewStatus === 'completed' || showGoodbye) {
    if (expectsCodingPhase && typeof onContinueToCoding === 'function') {
      return (
        <div style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0F172A, #1E293B)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{
            textAlign: 'center',
            padding: '48px 40px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 24,
            border: '1px solid rgba(255,255,255,0.1)',
            maxWidth: 480,
            width: '90%',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>1 / 2</div>
            <h1 style={{
              fontSize: 24,
              fontWeight: 800,
              color: 'white',
              marginBottom: 8,
            }}>
              Interview complete
            </h1>
            <p style={{
              fontSize: 15,
              color: '#94A3B8',
              marginBottom: 28,
              lineHeight: 1.6,
            }}>
              Next is the coding round. Same session — your camera and proctoring continue.
            </p>
            <button
              type="button"
              onClick={() => {
                const rec = videoRecorderRef.current;
                stashContinuousSessionHandoff({
                  videoRecorder: rec,
                  recordingStartTime: rec?.getRecordingStartTime?.() || recordingStartTime,
                  stream: prepResourcesRef.current?.stream || videoElementRef.current?.srcObject || null,
                });
                // Prevent unmount cleanup from treating this as a normal end.
                videoRecorderRef.current = null;
                onContinueToCoding();
              }}
              style={{
                background: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '14px 28px',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Continue to coding
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0F172A, #1E293B)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '48px 40px',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 24,
          border: '1px solid rgba(255,255,255,0.1)',
          maxWidth: 480,
          width: '90%',
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>
            🎉
          </div>
          <h1 style={{
            fontSize: 26,
            fontWeight: 800,
            color: 'white',
            marginBottom: 8,
          }}>
            Well done{candidateName ? `, ${candidateName.split(' ')[0]}` : ''}!
          </h1>
          <p style={{
            fontSize: 15,
            color: '#94A3B8',
            marginBottom: 32,
            lineHeight: 1.6,
          }}>
            You've completed your interview.
            Your responses have been recorded
            and will be reviewed by the team.
          </p>

          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 14,
            padding: '20px 24px',
            marginBottom: 28,
            textAlign: 'left',
          }}>
            <div style={{
              fontSize: 11,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              fontWeight: 700,
              marginBottom: 14,
            }}>
              What happens next
            </div>
            {[
              '📋 Your answers are being reviewed by AI',
              '👥 The hiring team will evaluate results',
              '📧 You\'ll hear back within 2-3 business days',
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: i < 2 ? 10 : 0,
                fontSize: 13,
                color: '#CBD5E1',
                lineHeight: 1.5,
              }}>
                {item}
              </div>
            ))}
          </div>

          <p style={{
            fontSize: 12,
            color: '#475569',
          }}>
            You may safely close this window.
          </p>
        </div>
      </div>
    );
  }

  if (interviewStatus === 'error') {
    return (
      <div className="interview-room">
        <div className="error-state">
          <h2>Connection Error</h2>
          <p>Unable to connect to interview. Please try again.</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const showReconnecting =
    shouldReconnect && interviewStatus !== 'completed' && !showGoodbye
    && (!browserOnline || (everConnectedRef.current && !connected));

  return (
    <div className="interview-room interview-room--proto-stage">
      {showReconnecting && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
            background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 8,
            padding: '10px 16px', fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,.12)',
            maxWidth: 'calc(100vw - 32px)', textAlign: 'center',
          }}
        >
          {browserOnline ? 'Reconnecting…' : 'You are offline — reconnecting when your internet is back…'}
          <span style={{ fontWeight: 400 }}> Your answers are saved. Please keep this tab open.</span>
        </div>
      )}
      {/* Notification System - Top of page */}
      <div className="notification-container">
        {notifications.map(notification => (
          <div key={notification.id} className="notification-toast">
            ⚠️ {notification.message}
          </div>
        ))}
      </div>

      {/* Fullscreen enforcement overlay */}
      {!isFullscreen && interviewStatus === 'ready' && !showingClosingTTS && !showGoodbye && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 18, padding: '44px 52px',
            textAlign: 'center', maxWidth: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#EEF2FF', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" width="30" height="30">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Fullscreen Required
            </div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 28 }}>
              This interview must be conducted in fullscreen mode to ensure exam integrity. Please enter fullscreen to continue.
            </div>
            <button
              onClick={async () => {
                await requestInterviewFullscreen();
              }}
              style={{
                background: '#6366F1', color: '#fff', border: 'none',
                borderRadius: 10, padding: '14px 36px', fontSize: 15,
                fontWeight: 600, cursor: 'pointer', width: '100%',
              }}
            >
              Enter Fullscreen
            </button>
          </div>
        </div>
      )}

      {/* Screen-share gate (after fullscreen) */}
      {isFullscreen && showScreenShareGate && interviewStatus === 'ready' && !showingClosingTTS && !showGoodbye && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 18, padding: '44px 52px',
            textAlign: 'center', maxWidth: 440, boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
              Screen Share Required
            </div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 28 }}>
              Share your <strong>entire screen</strong> (not a window or tab). Stopping screen share will pause the interview until you share again.
            </div>
            <button
              onClick={handleRestoreScreenShare}
              style={{
                background: '#6366F1', color: '#fff', border: 'none',
                borderRadius: 10, padding: '14px 36px', fontSize: 15,
                fontWeight: 600, cursor: 'pointer', width: '100%',
              }}
            >
              Share Entire Screen
            </button>
          </div>
        </div>
      )}

      {/* In-app End Interview Confirmation Modal (preserves HTML5 fullscreen) */}
      {showEndConfirmModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="end-interview-confirm-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(15, 23, 42, 0.78)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setShowEndConfirmModal(false)}
        >
          <div
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 18,
              padding: '36px 32px 30px',
              maxWidth: 430,
              width: '100%',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              textAlign: 'center',
              color: '#f8fafc',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: '#f87171',
              }}
            >
              <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden>
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1 0-1.41C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.39.39.39 1.02 0 1.41l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
              </svg>
            </div>

            <div
              id="end-interview-confirm-title"
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#f8fafc',
                marginBottom: 10,
              }}
            >
              End Interview?
            </div>

            <div
              style={{
                fontSize: 14,
                color: '#94a3b8',
                lineHeight: 1.6,
                marginBottom: 28,
              }}
            >
              Are you sure you want to end the interview? This action cannot be undone and your responses will be submitted.
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                onClick={() => setShowEndConfirmModal(false)}
                style={{
                  flex: 1,
                  background: '#334155',
                  color: '#f8fafc',
                  border: '1px solid #475569',
                  borderRadius: 10,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#475569'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#334155'; }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmEndInterview}
                style={{
                  flex: 1,
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)',
                  transition: 'background 0.15s ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#b91c1c'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#dc2626'; }}
              >
                End Interview
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="interview-header interview-header--question-top">
        <div className="interview-top-question" />
        <div className="connection-status">
          <span className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? '●' : '○'} {connected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* OpenAIRealtimeRecorder — default candidate-facing recorder
          (STT_PROVIDER=openai_realtime), mounted once for the whole
          interview, not remounted per question. Same prop contract as
          VoiceRecorder.jsx, including lastSpokenText (fold-time AI-echo
          check via looksLikeAiEcho — see the header comment in
          OpenAIRealtimeRecorder.jsx), plus the added sessionId/accessToken
          it needs to mint its own realtime client secret
          (app/api/realtime_stt.py). */}
      {/* Active Speech-to-Text Recorder:
          Primary: OpenAI Realtime STT (WebRTC).
          Automatic Fallback: Switches to VoiceRecorder (browser Web Speech API)
          if OpenAI Realtime token mint, SDP, or WebRTC connection fails. */}
      {currentQuestion && !(interviewStatus === 'completed' || showGoodbye) && (
        useLocalSttFallback ? (
          <VoiceRecorder
            isRecording={waitingForAnswer && isRecording && !readyToSubmit}
            aiSpeaking={raniSpeaking}
            onStart={handleStartRecording}
            onStop={handleVoiceRecorderStop}
            onTranscript={handleTranscript}
            onAudioBlob={handleAudioBlob}
            onSilenceDetected={handleSilenceDetected}
            silenceThresholdMs={getSilenceThresholdMs(currentQuestion)}
            lastSpokenText={lastSpokenTextRef.current}
            disabled={false}
            interviewCompleted={interviewStatus === 'completed' || showGoodbye}
            showTranscript={false}
            language={currentQuestion?.language || 'en'}
            micEnabled={controlMicOn}
          />
        ) : (
          <OpenAIRealtimeRecorder
            isRecording={waitingForAnswer && isRecording && !readyToSubmit}
            aiSpeaking={raniSpeaking}
            onStart={handleStartRecording}
            onStop={handleVoiceRecorderStop}
            onTranscript={handleTranscript}
            onAudioBlob={handleAudioBlob}
            onSilenceDetected={handleSilenceDetected}
            silenceThresholdMs={getSilenceThresholdMs(currentQuestion)}
            lastSpokenText={lastSpokenTextRef.current}
            disabled={false}
            interviewCompleted={interviewStatus === 'completed' || showGoodbye}
            showTranscript={false}
            language={currentQuestion?.language || 'en'}
            micEnabled={controlMicOn}
            sessionId={sessionId}
            accessToken={accessToken}
            onError={handleRealtimeSttError}
          />
        )
      )}

      {/* Question Card Fixed to the Right of David's Centered Circle */}
      {currentQuestion && !showingClosingTTS && (
        <div
          className={`interview-right-question-panel${
            questionTransition === 'in' ? ' question-fade-in' : ' question-fade-out'
          }`}
        >
          <QuestionDisplay
            question={currentQuestion}
            questionAudioElement={questionAudioElementRef.current}
            totalQuestions={totalQuestions}
            variant="sideCard"
            onQuestionComplete={beginListeningForAnswer}
          />
        </div>
      )}

      <div className="interview-main-layout">
        <div className="interview-left-camera interview-left-stage">
          <AIInterviewer
            raniSpeaking={raniSpeaking}
            showingClosingTTS={showingClosingTTS}
            processingAnswer={processingAnswer}
            waitingForAnswer={waitingForAnswer}
            isRecording={isRecording}
            questionType={currentQuestion?.question_type || currentQuestion?.type || ''}
            videoUrl={raniVideoUrl}
            onVideoEnded={() => setRaniVideoUrl(null)}
            audioElement={questionAudioElementRef.current}
            davidAnalyser={videoRecorderRef.current?.getDavidAnalyser?.() || null}
            micAnalyser={videoRecorderRef.current?.getMicAnalyser?.() || null}
            readyToSubmit={readyToSubmit}
            onSilenceDone={handleSilenceDetected}
            onSubmitAnswer={() => {
              setReadyToSubmit(false);
              handleEndAnswer(transcriptAccumulatorRef.current);
            }}
            isWhisperLoading={isWhisperLoading}
            recordingSecondsLeft={recordingSecondsLeft}
            idleSeconds={idleSeconds}
            statusLabel={
              showingClosingTTS && closingTtsPreparing
                ? 'Wrapping up your interview…'
                : systemWorkingNote || null
            }
            speechTranscriptText={davidSpeechTranscript}
          />

          <div className="candidate-camera-pip" id="candidateCameraPip">
            <ProctoringMonitor
              sessionId={sessionId}
              recordingStartTime={recordingStartTime}
              showCameraOnly={true}
              onVideoReady={handleVideoReady}
              videoRecorderRef={videoRecorderRef}
              prewarmedStream={prepResources?.stream}
              prewarmedReady={hasLivePrewarm}
              manageBrowserEvents={false}
              preserveStreamOnUnmount={expectsCodingPhase}
            />
            <span className="candidate-camera-pip-label">You</span>
          </div>
        </div>
      </div>

      {!showingClosingTTS && !showGoodbye && (
        <footer className="interview-control-bar" aria-label="Interview controls">
          <p className="interview-control-note">
            Please do not refresh the page or you&apos;ll lose the data.
          </p>
          <div className="interview-control-buttons">
            <button
              type="button"
              className={`interview-ctrl-btn${controlMicOn ? ' is-active' : ' is-off'}`}
              title={controlMicOn ? 'Mute microphone' : 'Unmute microphone'}
              aria-label={controlMicOn ? 'Mute microphone' : 'Unmute microphone'}
              aria-pressed={controlMicOn}
              onClick={toggleControlMic}
            >
              {controlMicOn ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`interview-ctrl-btn${controlCamOn ? ' is-active' : ' is-off'}`}
              title={controlCamOn ? 'Turn camera off' : 'Turn camera on'}
              aria-label={controlCamOn ? 'Turn camera off' : 'Turn camera on'}
              aria-pressed={controlCamOn}
              onClick={toggleControlCam}
            >
              {controlCamOn ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`interview-ctrl-btn${!showScreenShareGate ? ' is-active' : ' is-off'}`}
              title={
                !showScreenShareGate && isStreamLive(screenStreamRef.current)
                  ? 'Screen sharing active'
                  : 'Share entire screen'
              }
              aria-label={
                !showScreenShareGate && isStreamLive(screenStreamRef.current)
                  ? 'Screen sharing active'
                  : 'Share entire screen'
              }
              aria-pressed={!showScreenShareGate}
              onClick={handleControlScreenShare}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </button>
            <button
              type="button"
              className="interview-ctrl-btn interview-ctrl-btn--end"
              title="End interview"
              aria-label="End interview"
              disabled={interviewStatus === 'completed' || isEndingInterview}
              onClick={handleEndInterviewClick}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1 0-1.41C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.39.39.39 1.02 0 1.41l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
              </svg>
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default CandidateInterviewRoom;

